import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    IconCheck,
    IconChevronDown,
    IconPencil,
    IconPlus,
    IconSparkles,
    IconTrash,
    IconWand,
    IconX,
} from '@tabler/icons-react';
import { useSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router-dom';

import AIAgentOrb from 'src/components/AIAgentOrb';
import ManagerGoalChecklist, { DEFAULT_MANAGER_GOALS, normalizeManagerGoals, fieldsForGoals, type ManagerGoalId } from './ManagerGoalChecklist';
import Checked from 'src/icons/Checked';
import RotateLeftS from 'src/icons/RotateLeftS';
import { DesktopLayout } from 'src/layout';
import AIManagersService, {
    AI_MANAGER_GOAL_LABELS,
    AIManager,
    AIManagerDisplayKnowledgeItem,
    AIManagerDisplayKnowledgeProfile,
    AIManagerDisplayKnowledgeSection,
    AIManagerFieldProgress,
    AIManagerGoal,
    AIManagerKnowledgeAnalysis,
    AIManagerKnowledgeDocument,
    AIManagerKnowledgeFacts,
    AIManagerKnowledgeSectionKey,
    AIManagerFollowUpRule,
    AIManagerQualificationField,
} from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';
import useUserStore from 'src/stores/user';
import LeadStreamsService from 'src/services/LeadStreamsService';
import { ROUTES_URL } from 'src/constants/routes';
import { getAIManagerAvatarPalette } from 'src/utils/aiManagers';
import {
    getStreamAccessLabel,
    getStreamDisplayName,
    hasPaidStreamHistory,
    normalizeStreamDisplayName,
} from 'src/utils/leadStreams';
import { history } from 'src/utils/history';

import useKnowledgeAnalysis from './useKnowledgeAnalysis';
import KnowledgeAnalysisState from './KnowledgeAnalysisState';
import useKnowledgeLibrary from './useKnowledgeLibrary';
import KnowledgeLibraryState from './KnowledgeLibraryState';

import type { Stream } from 'src/services/LeadStreamsService/agent';

type SetupStep = 'name' | 'knowledge' | 'scenario' | 'test';

type DialogStyle = 'expert' | 'friendly' | 'direct';

type ScriptTemplateId = 'sales_qualification' | 'sales_consulting' | 'share_channel' | 'explain_service';

type KnowledgeMode = 'existing' | 'website';

type RouteParams = {
    managerId?: string;
};

const SETUP_STEPS: Array<{ key: SetupStep; title: string; caption: string }> = [
    { key: 'name', title: 'Имя', caption: 'Кто будет отвечать' },
    { key: 'knowledge', title: 'База знаний', caption: 'Сайт или готовая база' },
    { key: 'scenario', title: 'Сценарий', caption: 'Стиль, цель и шаблон' },
    { key: 'test', title: 'Тест', caption: 'Проверка диалога' },
];

type CreationChecklistItem = {
    label: string;
    done: boolean;
};

const KNOWLEDGE_SECTIONS: Array<{ key: AIManagerKnowledgeSectionKey; title: string; empty: string }> = [
    { key: 'offer', title: 'Что вы предлагаете', empty: 'Добавьте продукт, услугу или основной оффер' },
    { key: 'audience', title: 'Кому подходит', empty: 'Добавьте сегменты потенциальных клиентов' },
    { key: 'value', title: 'Почему выбирают', empty: 'Добавьте преимущества, кейсы или доказательства' },
    { key: 'process', title: 'Как работает', empty: 'Добавьте этапы работы или процесс подключения' },
    { key: 'prices', title: 'Цены и условия оплаты', empty: 'Добавьте цены, тарифы или правило расчета' },
    { key: 'constraints', title: 'Ограничения', empty: 'Добавьте условия, ограничения и важные правила' },
    { key: 'contacts', title: 'Контакты', empty: 'Добавьте телефон, email или ссылку для связи' },
];

const DEFAULT_FIELDS: Record<AIManagerGoal, AIManagerQualificationField[]> = {
    qualify: [
        {
            id: 'task',
            label: 'Задача',
            question: 'Что нужно сделать или какую проблему решить',
            required: true,
        },
        {
            id: 'timeline',
            label: 'Сроки',
            question: 'Когда нужен результат',
            required: false,
        },
        {
            id: 'budget',
            label: 'Бюджет',
            question: 'Какой бюджет планируется',
            required: false,
        },
        {
            id: 'contact',
            label: 'Контакт',
            question: 'Куда передать детали для связи',
            required: true,
        },
    ],
    recommend_service: [
        {
            id: 'need',
            label: 'Потребность',
            question: 'Что клиент хочет решить',
            required: true,
        },
        {
            id: 'context',
            label: 'Контекст',
            question: 'Что уже пробовали и в каком формате нужен результат',
            required: false,
        },
        {
            id: 'constraints',
            label: 'Ограничения',
            question: 'Есть ли важные ограничения',
            required: false,
        },
        {
            id: 'budget',
            label: 'Бюджет',
            question: 'Какой бюджет комфортен',
            required: false,
        },
        {
            id: 'contact',
            label: 'Контакт',
            question: 'Куда передать подходящий вариант',
            required: false,
        },
    ],
};

const GOAL_DESCRIPTIONS: Record<AIManagerGoal, string> = {
    qualify: 'Выяснить детали запроса потенциального клиента и подготовить его к передаче человеку',
    recommend_service: 'Понять интерес потенциального клиента и коротко рассказать про сайт, канал или продукт',
};

const STYLE_OPTIONS: Array<{ id: DialogStyle; title: string; description: string }> = [
    {
        id: 'expert',
        title: 'Экспертный',
        description: 'Спокойно объясняет, уточняет детали и не давит',
    },
    {
        id: 'friendly',
        title: 'Дружелюбный',
        description: 'Пишет мягко, живо и помогает разобраться',
    },
    {
        id: 'direct',
        title: 'Короткий',
        description: 'Быстро задает вопросы и ведет к следующему шагу',
    },
];

const SCRIPT_TEMPLATES: Record<
    AIManagerGoal,
    Array<{
        id: ScriptTemplateId;
        title: string;
        description: string;
        openingMessage: string;
        flowSteps: string[];
        handoffRule: string;
    }>
> = {
    qualify: [
        {
            id: 'sales_qualification',
            title: 'Продажная квалификация',
            description: 'Задача, сроки, бюджет и контакт для передачи человеку',
            openingMessage:
                'Здравствуйте! Видел ваш запрос. Уточню задачу, сроки и бюджет, чтобы передать детали специалисту',
            flowSteps: ['Понять запрос', 'Уточнить задачу, сроки, бюджет и контакт', 'Передать детали человеку'],
            handoffRule:
                'Передать человеку, если потенциальный клиент готов обсудить цену, договор, счет или нестандартные условия',
        },
        {
            id: 'sales_consulting',
            title: 'Консультационная продажа',
            description: 'Сначала помогает разобраться, затем аккуратно переводит к обсуждению',
            openingMessage:
                'Здравствуйте! Видел ваш запрос. Помогу разобраться и уточню пару деталей, чтобы предложить следующий шаг',
            flowSteps: ['Понять контекст', 'Уточнить задачу и ограничения', 'Предложить следующий шаг'],
            handoffRule:
                'Передать человеку, если потенциальный клиент просит расчет, условия, договор или консультацию',
        },
    ],
    recommend_service: [
        {
            id: 'share_channel',
            title: 'Рекомендация канала',
            description: 'Похоже на живую рекомендацию: что посмотреть и почему это может помочь',
            openingMessage:
                'Здравствуйте! Вы спрашивали про это. Я пользовался этим Telegram-каналом, там есть полезные разборы и примеры по теме',
            flowSteps: ['Понять интерес', 'Показать, что полезного есть в источнике', 'Дать ссылку или следующий шаг'],
            handoffRule:
                'Передать человеку, если потенциальный клиент просит контакт, цену, доступ или индивидуальный совет',
        },
        {
            id: 'explain_service',
            title: 'Объяснить продукт',
            description: 'Коротко рассказывает, что это, кому подходит и где посмотреть подробнее',
            openingMessage:
                'Здравствуйте! Вы спрашивали про это. Коротко расскажу, чем может быть полезно, и подскажу, где посмотреть детали',
            flowSteps: ['Понять вопрос', 'Объяснить суть и пользу', 'Подсказать источник или следующий шаг'],
            handoffRule:
                'Передать человеку, если потенциальный клиент просит персональную консультацию или точные условия',
        },
    ],
};

const STOP_PHRASES = ['не пишите', 'не интересно', 'отстаньте', 'стоп', 'удалите мой контакт'];
const DEFAULT_FOLLOW_UPS: AIManagerFollowUpRule[] = [
    {
        id: 'no-reply-24h',
        reason: 'Потенциальный клиент не ответил',
        delayHours: 24,
        message: 'Здравствуйте! Подскажите, вопрос ещё актуален? Могу уточнить детали и передать специалисту',
    },
];
const TEST_MODEL = 'gpt-4o-mini';
const TEST_MAX_OUTPUT_TOKENS = 420;
const TEST_MAX_MESSAGES = 6;
const TEST_WAITING_TITLE = 'Готовим ответ';
const TEST_WAITING_HINT = 'Обычно до минуты';
const LOCAL_SETUP_DRAFT_KEY = 'leadsmania2_ai_manager_setup_v2_draft';
const BORDER = 'var(--lm-border)';
const PANEL_RADIUS = '18px';
const CARD_RADIUS = '12px';

const KNOWLEDGE_FACT_TYPE_LABELS: Record<string, string> = {
    usp: 'УТП',
    client: 'Клиент',
    service: 'Услуга',
    price: 'Цена',
    artifact: 'Артефакт',
    geography: 'География',
    constraint: 'Ограничение',
    contact: 'Контакт',
    source_note: 'Заметка',
    manual: 'Вручную',
};

const KNOWLEDGE_PREVIEW_HEADINGS = new Set([
    'База знаний',
    'Профиль компании',
    ...KNOWLEDGE_SECTIONS.map((section) => section.title),
]);

function handleKeyboardActivation(onActivate: () => void, disabled = false) {
    return (event: KeyboardEvent<HTMLElement>) => {
        if (disabled || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
        }

        event.preventDefault();
        onActivate();
    };
}

function normalizeSetupStep(value?: string | null): SetupStep {
    if (value === 'knowledge' || value === 'scenario' || value === 'test') {
        return value;
    }
    return 'name';
}

function selectedStreamName(stream: Stream) {
    return normalizeStreamDisplayName(getStreamDisplayName(stream)).replace(
        'Поток лидов',
        'Поток потенциальных клиентов'
    );
}

function generateManagerName(streams: Stream[], selectedIds: number[]) {
    const selected = streams.filter((stream) => selectedIds.includes(stream.id));
    if (!selected.length) {
        return '';
    }
    const firstName = selectedStreamName(selected[0]);
    return selected.length === 1 ? `AI-менеджер · ${firstName}` : `AI-менеджер · ${firstName} +${selected.length - 1}`;
}

function setupStepIndex(step: SetupStep) {
    return SETUP_STEPS.findIndex((item) => item.key === step);
}

function hasKnowledgeItems(profile: AIManagerDisplayKnowledgeProfile | null) {
    return Boolean(profile?.sections.some((section) => section.items.some((item) => item.text.trim())));
}

function createEmptyDisplayProfile(): AIManagerDisplayKnowledgeProfile {
    return {
        summary: '',
        sections: KNOWLEDGE_SECTIONS.map((section) => ({ key: section.key, title: section.title, items: [] })),
        missingSections: KNOWLEDGE_SECTIONS.map((section) => section.key),
        warnings: [],
    };
}

function withCompleteSections(profile: AIManagerDisplayKnowledgeProfile | null): AIManagerDisplayKnowledgeProfile {
    const source = profile || createEmptyDisplayProfile();
    return {
        ...source,
        sections: KNOWLEDGE_SECTIONS.map((section) => {
            const existing = source.sections.find((item) => item.key === section.key);
            return existing || { key: section.key, title: section.title, items: [] };
        }),
    };
}

function toBackendDisplayProfile(profile: AIManagerDisplayKnowledgeProfile) {
    return {
        summary: profile.summary,
        sections: profile.sections.map((section) => ({
            key: section.key,
            title: section.title,
            items: section.items.map((item) => ({
                id: item.id,
                text: item.text,
                origin: item.origin,
                source_url: item.sourceUrl || '',
                source_snippet: item.sourceSnippet || '',
                confidence: item.confidence,
                type: item.type || '',
            })),
        })),
        missing_sections: profile.sections.filter((section) => !section.items.length).map((section) => section.key),
        warnings: profile.warnings,
    };
}

function buildKnowledgeFactsFromProfile(profile: AIManagerDisplayKnowledgeProfile): AIManagerKnowledgeFacts {
    const facts: AIManagerKnowledgeFacts = {
        usp: [],
        clients: [],
        services: [],
        prices: [],
        artifacts: [],
        geography: [],
        constraints: [],
        sourceNotes: [],
        contacts: {
            phones: [],
            emails: [],
            links: [],
        },
        typedItems: [],
    };

    const pushUnique = (target: string[], value: string) => {
        const text = value.trim();
        if (text && !target.includes(text)) {
            target.push(text);
        }
    };

    profile.sections.forEach((section) => {
        section.items.forEach((item) => {
            const text = item.text.trim();
            if (!text) {
                return;
            }

            if (section.key === 'value') pushUnique(facts.usp, text);
            if (section.key === 'audience') pushUnique(facts.clients, text);
            if (section.key === 'offer') pushUnique(facts.services, text);
            if (section.key === 'prices') pushUnique(facts.prices, text);
            if (section.key === 'process') pushUnique(facts.artifacts, text);
            if (section.key === 'constraints') pushUnique(facts.constraints, text);
            if (section.key === 'contacts') {
                if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
                    pushUnique(facts.contacts.emails, text);
                } else if (/^https?:\/\//i.test(text) || text.startsWith('@')) {
                    pushUnique(facts.contacts.links, text);
                } else if (/^\+?[\d\s()\\-]{7,}$/.test(text)) {
                    pushUnique(facts.contacts.phones, text);
                } else {
                    pushUnique(facts.contacts.links, text);
                }
            }

            facts.typedItems.push({
                type: item.type || section.key,
                label: section.title,
                value: text,
                sourceUrl: item.sourceUrl,
                sourceSnippet: item.sourceSnippet,
                confidence: item.confidence,
                isUseful: true,
            });
        });
    });

    return facts;
}

function buildKnowledgeBody(profile: AIManagerDisplayKnowledgeProfile, offerNotes: string) {
    const lines: string[] = ['# Профиль компании', ''];
    profile.sections.forEach((section) => {
        if (!section.items.length) {
            return;
        }
        lines.push(`## ${section.title}`);
        section.items.forEach((item) => lines.push(`- ${item.text}`));
        lines.push('');
    });
    const notes = offerNotes.trim();
    if (notes) {
        lines.push('## Важные заметки пользователя');
        lines.push(`- ${notes}`);
        lines.push('');
    }
    return lines.join('\n').trim();
}

function normalizeDisplayProfileFromDocument(document: AIManagerKnowledgeDocument): AIManagerDisplayKnowledgeProfile {
    const metadata = document.metadata || {};
    const rawProfile =
        metadata.approved_profile ||
        metadata.approvedProfile ||
        metadata.knowledge_display_profile ||
        metadata.knowledgeDisplayProfile;

    if (rawProfile && typeof rawProfile === 'object') {
        const source = rawProfile as Record<string, unknown>;
        const sections = Array.isArray(source.sections) ? source.sections : [];
        return withCompleteSections({
            summary: String(source.summary || document.summary || '').trim(),
            sections: sections
                .map((section) => {
                    const rawSection =
                        section && typeof section === 'object' ? (section as Record<string, unknown>) : {};
                    const key = String(rawSection.key || '').trim() as AIManagerKnowledgeSectionKey;
                    const title =
                        KNOWLEDGE_SECTIONS.find((item) => item.key === key)?.title ||
                        String(rawSection.title || '').trim();

                    if (!key || !title) {
                        return null;
                    }

                    const items = Array.isArray(rawSection.items) ? rawSection.items : [];
                    return {
                        key,
                        title,
                        items: items
                            .map((item, index) => {
                                const rawItem =
                                    item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
                                const text = cleanKnowledgePreviewText(String(rawItem.text || '').trim());
                                const origin = String(rawItem.origin || '').trim();

                                if (!text) {
                                    return null;
                                }

                                const confidence = Number(rawItem.confidence);
                                return {
                                    id: String(rawItem.id || `doc-${document.id}-${key}-${index}`),
                                    text,
                                    origin: origin === 'manual' || origin === 'fallback' ? origin : 'website',
                                    sourceUrl: String(
                                        rawItem.source_url || rawItem.sourceUrl || document.sourceUrl || ''
                                    ).trim(),
                                    sourceSnippet: String(rawItem.source_snippet || rawItem.sourceSnippet || '').trim(),
                                    confidence: Number.isFinite(confidence) ? confidence : undefined,
                                    type: String(rawItem.type || '').trim() || undefined,
                                };
                            })
                            .filter(Boolean) as AIManagerDisplayKnowledgeItem[],
                    };
                })
                .filter(Boolean) as AIManagerDisplayKnowledgeSection[],
            missingSections: [],
            warnings: [],
        });
    }

    const text = cleanKnowledgePreviewText(document.summary || document.body);
    const profile = createEmptyDisplayProfile();
    if (text) {
        profile.sections[0].items.push({
            id: `doc-${document.id}-summary`,
            text: text.slice(0, 700),
            origin: 'manual',
            sourceUrl: document.sourceUrl,
            type: 'source_note',
        });
    }
    return profile;
}

function normalizeKnowledgeSourceUrl(value?: string | null) {
    const source = String(value || '').trim();
    if (!source) {
        return '';
    }
    return /^https?:\/\//i.test(source) ? source : `https://${source}`;
}

function getApiErrorMessage(error: unknown, fallback: string) {
    const requestError = error as { code?: string; message?: string; response?: { status?: number; data?: unknown } };
    const message = String(requestError?.message || '').toLowerCase();
    if (requestError?.code === 'ECONNABORTED' || message.includes('timeout')) {
        return 'Сервер не успел ответить. Повторите запрос';
    }

    const status = requestError?.response?.status;
    if (status === 504) {
        return 'Сервер не успел получить ответ. Повторите запрос';
    }
    if (status === 502 || status === 503) {
        return 'Сервис временно недоступен. Повторите позже';
    }

    const data = requestError?.response?.data;
    if (typeof data === 'string') {
        return data;
    }
    if (data && typeof data === 'object') {
        const source = data as Record<string, unknown>;
        if (typeof source.detail === 'string') {
            return source.detail;
        }
        const first = Object.entries(source).find(([, value]) => {
            if (typeof value === 'string') {
                return Boolean(value.trim());
            }
            return Array.isArray(value) && value.length > 0;
        });
        if (first) {
            const [field, value] = first;
            const message = Array.isArray(value) ? String(value[0]) : String(value);
            return `${field}: ${message}`;
        }
    }
    return fallback;
}

function getHttpStatus(error: unknown) {
    return (error as { response?: { status?: number } })?.response?.status;
}

function formatKnowledgeItemType(type?: string) {
    const key = String(type || '').trim();
    return KNOWLEDGE_FACT_TYPE_LABELS[key] || key || 'Факт';
}

function getKnowledgeItemCount(profile: AIManagerDisplayKnowledgeProfile | null) {
    return withCompleteSections(profile).sections.reduce(
        (sum, section) => sum + section.items.filter((item) => item.text.trim()).length,
        0
    );
}

function formatKnowledgeSourceLabel(value?: string | null) {
    return (
        String(value || '')
            .replace(/^https?:\/\//, '')
            .replace(/\/$/, '') || 'Без источника'
    );
}

function formatCompactDate(value?: string | null) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function cleanKnowledgePreviewText(value?: string | null) {
    const text = String(value || '')
        .split(/\n+/)
        .map((line) =>
            line
                .replace(/#{1,6}\s*/g, '')
                .replace(/(^|\s)[-*]\s*/g, '$1')
                .replace(/\*\*/g, '')
                .trim()
        )
        .filter((line) => line && line !== '-')
        .join(' · ');
    let cleanedText = text;
    KNOWLEDGE_PREVIEW_HEADINGS.forEach((heading) => {
        const escapedHeading = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        cleanedText = cleanedText.replace(
            new RegExp(`(^|\\s*·\\s*|\\s+)${escapedHeading}(?=\\s|\\s*·\\s*|$)`, 'gi'),
            ' · '
        );
    });
    return cleanedText
        .split(/\s*·\s*/)
        .map((part) => part.trim())
        .filter((part) => part && !KNOWLEDGE_PREVIEW_HEADINGS.has(part))
        .join(' · ');
}

function getKnowledgeDocumentPreview(profile: AIManagerDisplayKnowledgeProfile, fallback?: string | null) {
    const facts = withCompleteSections(profile)
        .sections.flatMap((section) => section.items.map((item) => cleanKnowledgePreviewText(item.text)))
        .filter(Boolean)
        .slice(0, 3);
    return facts.length ? facts.join(' · ') : cleanKnowledgePreviewText(fallback).slice(0, 240);
}

function getDraftStep(manager: AIManager) {
    return normalizeSetupStep(String(manager.metadata.setup_step || manager.metadata.setupStep || 'name'));
}

function isSetupV2Draft(manager: AIManager) {
    const version = String(manager.metadata.setup_version || manager.metadata.setupVersion || '');
    const setupCompleted = manager.metadata.setup_completed === true || manager.metadata.setupCompleted === true;
    return (
        version === '2' &&
        !setupCompleted &&
        !manager.hasLaunched &&
        manager.status !== 'ready_to_launch' &&
        manager.status !== 'running' &&
        manager.status !== 'paused'
    );
}

function serializeLocalDraft({
    managerName,
    selectedStreamIds,
    goal,
    fields,
    goalChecklist,
    dialogStyle,
    scriptTemplate,
    websiteUrl,
    offerNotes,
    knowledgeMode,
    displayProfile,
    selectedKnowledgeDocumentId,
    testRating,
    testImprovement,
}: {
    managerName: string;
    selectedStreamIds: number[];
    goal: AIManagerGoal;
    fields: AIManagerQualificationField[];
    goalChecklist: ManagerGoalId[];
    dialogStyle: DialogStyle;
    scriptTemplate: ScriptTemplateId;
    websiteUrl: string;
    offerNotes: string;
    knowledgeMode: KnowledgeMode;
    displayProfile: AIManagerDisplayKnowledgeProfile | null;
    selectedKnowledgeDocumentId: number | null;
    testRating: number | null;
    testImprovement: string;
}) {
    return {
        managerName,
        selectedStreamIds,
        goal,
        fields,
        goalChecklist,
        dialogStyle,
        scriptTemplate,
        websiteUrl,
        offerNotes,
        knowledgeMode,
        displayProfile,
        selectedKnowledgeDocumentId,
        testRating,
        testImprovement,
        updatedAt: new Date().toISOString(),
    };
}

function buildFieldProgressFromLocal(fields: AIManagerQualificationField[], knownFacts: Record<string, unknown>) {
    const normalizeKey = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '_');
    const knownKeys = Object.keys(knownFacts).reduce<Record<string, unknown>>((acc, key) => {
        const value = knownFacts[key];
        if (value !== undefined && value !== null && value !== '') {
            acc[normalizeKey(key)] = value;
        }
        return acc;
    }, {});
    const progressFields = fields.map((field) => {
        const value = knownKeys[normalizeKey(field.id)] || knownKeys[normalizeKey(field.label)];
        return {
            id: field.id,
            label: field.label,
            required: field.required,
            collected: value !== undefined && value !== null && value !== '',
            missing: field.required && (value === undefined || value === null || value === ''),
            value,
        };
    });
    const required = progressFields.filter((field) => field.required);
    return {
        fields: progressFields,
        requiredCount: required.length,
        collectedRequiredCount: required.filter((field) => field.collected).length,
        complete: required.length > 0 && required.every((field) => field.collected),
    };
}

function hasProgressValue(value: unknown) {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (value && typeof value === 'object') {
        return Object.keys(value).length > 0;
    }
    return value !== undefined && value !== null && String(value).trim() !== '';
}

function formatProgressValue(value: unknown) {
    if (Array.isArray(value)) {
        return value
            .map((item) => String(item || '').trim())
            .filter(Boolean)
            .join(', ');
    }
    if (value && typeof value === 'object') {
        return Object.values(value as Record<string, unknown>)
            .map((item) => String(item || '').trim())
            .filter(Boolean)
            .join(', ');
    }
    return String(value || '').trim();
}

function getFactValueByAliases(knownFacts: Record<string, unknown>, aliases: string[]) {
    const normalizeKey = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '_');
    const knownKeys = Object.keys(knownFacts || {}).reduce<Record<string, unknown>>((acc, key) => {
        const value = knownFacts[key];
        if (hasProgressValue(value)) {
            acc[normalizeKey(key)] = value;
        }
        return acc;
    }, {});

    return aliases
        .map(normalizeKey)
        .map((key) => knownKeys[key])
        .find(hasProgressValue);
}

function getUserDialogText(messages: Array<{ role: 'user' | 'assistant'; content: string }>) {
    return messages
        .filter((message) => message.role === 'user')
        .map((message) => message.content.trim())
        .filter(Boolean)
        .join('\n');
}

function inferProgressValueFromMessages(
    field: AIManagerQualificationField,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>
) {
    const text = getUserDialogText(messages);
    if (!text) {
        return undefined;
    }

    const key = `${field.id} ${field.label}`.toLowerCase();
    const findLine = (matcher: RegExp) =>
        text
            .split(/\n+/)
            .find((line) => matcher.test(line.toLowerCase()))
            ?.trim();

    if (/budget|бюджет|цена|стоимость|чек/.test(key)) {
        return findLine(/(\d[\d\s.,]*(?:тыс|к|k|млн|руб|₽|р\.|рублей)|бюджет|чек)/i);
    }
    if (/timeline|срок|старт|когда|deadline/.test(key)) {
        return findLine(
            /(срок|сентябр|октябр|ноябр|декабр|январ|феврал|март|апрел|ма[йя]|июн|июл|август|старт|запуск|через|недел|месяц|квартал)/i
        );
    }
    if (/contact|контакт|связ|телефон|email|почт/.test(key)) {
        return findLine(
            /([^\s@]+@[^\s@]+\.[^\s@]+|\+?\d[\d\s()\\-]{7,}|(?:^|\s)@\w{3,}|t\.me\/|telegram:\s*@|мой\s+тг|мой\s+telegram|связаться|для\s+связи)/i
        );
    }
    if (/task|need|задач|потребност|что нужно|проблем/.test(key)) {
        return findLine(/(нужно|ищу|хочу|задач|проблем|решени|получать|привлеч|запустить|сделать)/i);
    }
    if (/context|контекст|пробовали|формат/.test(key)) {
        return findLine(/(пробовал|использовал|сейчас|раньше|формат|канал|сайт|crm|команд)/i);
    }
    if (/constraint|огранич/.test(key)) {
        return findLine(/(огранич|нельзя|важно|только|не работаем|услов)/i);
    }

    return undefined;
}

function mergeFieldProgressWithDialog(
    sourceProgress: AIManagerFieldProgress,
    fields: AIManagerQualificationField[],
    knownFacts: Record<string, unknown>,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>
): AIManagerFieldProgress {
    const nextFields = sourceProgress.fields.map((progressField) => {
        if (progressField.collected) {
            return progressField;
        }

        const setupField = fields.find((field) => field.id === progressField.id || field.label === progressField.label);
        const aliases = [
            progressField.id,
            progressField.label,
            setupField?.id || '',
            setupField?.label || '',
            setupField?.label.replace('ё', 'е') || '',
        ].filter(Boolean);
        const factValue = getFactValueByAliases(knownFacts, aliases);
        const inferredValue = setupField ? inferProgressValueFromMessages(setupField, messages) : undefined;
        const value = hasProgressValue(factValue) ? factValue : inferredValue;
        const collected = hasProgressValue(value);

        return {
            ...progressField,
            collected,
            missing: progressField.required && !collected,
            value: collected ? value : progressField.value,
        };
    });
    const requiredFields = nextFields.filter((field) => field.required);

    return {
        fields: nextFields,
        requiredCount: requiredFields.length,
        collectedRequiredCount: requiredFields.filter((field) => field.collected).length,
        complete: requiredFields.length > 0 && requiredFields.every((field) => field.collected),
    };
}

function isFieldProgressForFields(
    progress: AIManagerFieldProgress | null,
    fields: AIManagerQualificationField[]
): progress is AIManagerFieldProgress {
    if (!progress || progress.fields.length !== fields.length) {
        return false;
    }

    const progressKeys = new Set(progress.fields.map((field) => `${field.id}:${field.label}`));
    return fields.every((field) => progressKeys.has(`${field.id}:${field.label}`));
}

function SetupProgress({
    activeStep,
    managerName,
    completed,
    creationChecklist,
    onReset,
}: {
    activeStep: SetupStep;
    managerName: string;
    completed: Record<SetupStep, boolean>;
    creationChecklist: CreationChecklistItem[];
    onReset: () => void;
}) {
    const activeIndex = setupStepIndex(activeStep);
    const completedCount = SETUP_STEPS.filter((step) => completed[step.key]).length;
    const currentStepNumber = Math.min(SETUP_STEPS.length, activeIndex + 1);
    const showCreationChecklist = activeStep === 'test' || completed.test;

    return (
        <Box
            sx={{
                borderRadius: PANEL_RADIUS,
                border: `1px solid ${BORDER}`,
                bgcolor: 'var(--lm-surface)',
                p: { xs: 2, md: 2.5 },
            }}
        >
            <Stack spacing={2.25}>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1.75}
                >
                    <AIAgentOrb
                        size={62}
                        state={completed.test ? 'ready' : 'building'}
                        palette={getAIManagerAvatarPalette('aurora')}
                    />
                    <Box minWidth={0}>
                        <Typography
                            sx={{
                                minHeight: 34,
                                fontSize: 23,
                                lineHeight: 1.15,
                                fontWeight: 900,
                                color: managerName ? 'var(--lm-text)' : 'transparent',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {managerName || 'AI-менеджер'}
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        mb={1.75}
                    >
                        <Typography sx={{ fontWeight: 800 }}>Подготовка менеджера</Typography>
                        <Chip
                            size="small"
                            label={`Шаг ${currentStepNumber} из ${SETUP_STEPS.length}`}
                            sx={{ fontWeight: 700, bgcolor: 'var(--lm-accent-soft)', color: 'var(--lm-accent)' }}
                        />
                    </Box>
                    <Box
                        sx={{
                            height: 8,
                            bgcolor: 'var(--lm-border)',
                            borderRadius: 999,
                            overflow: 'hidden',
                            mb: 1.75,
                        }}
                    >
                        <Box
                            sx={{
                                width: `${(completedCount / SETUP_STEPS.length) * 100}%`,
                                height: '100%',
                                bgcolor: 'var(--lm-accent)',
                                borderRadius: 999,
                                transition: 'width 180ms ease',
                            }}
                        />
                    </Box>
                    <Stack spacing={1.15}>
                        {SETUP_STEPS.map((step, index) => {
                            const done = completed[step.key];
                            const active = step.key === activeStep;
                            return (
                                <Box
                                    key={step.key}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    px={1.5}
                                    py={1.2}
                                    borderRadius="12px"
                                    sx={{
                                        border: `1px solid ${active ? 'var(--lm-accent-ink)' : BORDER}`,
                                        bgcolor: done
                                            ? 'var(--lm-success-bg)'
                                            : active
                                            ? 'var(--lm-accent-soft)'
                                            : 'var(--lm-canvas)',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {done ? (
                                            <Checked />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    bgcolor: active ? 'var(--lm-accent)' : 'var(--lm-border)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: active ? 'var(--lm-accent-contrast)' : 'var(--lm-muted)',
                                                    }}
                                                >
                                                    {index + 1}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box minWidth={0}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                fontWeight: done || active ? 700 : 500,
                                                color: done || active ? 'var(--lm-text)' : 'var(--lm-muted)',
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'var(--lm-muted)' }}
                                        >
                                            {step.caption}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>

                {showCreationChecklist && (
                    <Box
                        sx={{
                            borderTop: `1px solid ${BORDER}`,
                            pt: 1.75,
                        }}
                    >
                        <Typography sx={{ fontWeight: 850, mb: 1.25 }}>Чеклист создания</Typography>
                        <Stack spacing={1.05}>
                            {creationChecklist.map((item) => (
                                <Box
                                    key={item.label}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Box
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: '50%',
                                            bgcolor: item.done ? 'var(--lm-success-bg)' : 'var(--lm-border)',
                                            color: item.done ? 'var(--lm-success-ink)' : 'var(--lm-muted)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {item.done ? <IconCheck size={15} /> : null}
                                    </Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 750, lineHeight: 1.35 }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                <Button
                    type="button"
                    size="small"
                    variant="text"
                    startIcon={<RotateLeftS />}
                    onClick={onReset}
                    sx={{
                        alignSelf: 'flex-start',
                        minHeight: 28,
                        px: 0.75,
                        borderRadius: '9px',
                        color: 'var(--lm-muted)',
                        fontSize: 12,
                        fontWeight: 500,
                        textTransform: 'none',
                        '& .MuiButton-startIcon': {
                            mr: 0.5,
                            color: 'inherit',
                        },
                        '&:hover': {
                            color: 'var(--lm-text)',
                            bgcolor: 'var(--lm-hover)',
                        },
                    }}
                >
                    Начать заново
                </Button>
            </Stack>
        </Box>
    );
}

function KnowledgeItemRow({
    item,
    onDelete,
    onChange,
}: {
    item: AIManagerDisplayKnowledgeItem;
    onDelete: () => void;
    onChange: (value: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(item.text);

    useEffect(() => {
        setDraft(item.text);
    }, [item.text]);

    const save = () => {
        const text = draft.trim();
        if (text) {
            onChange(text);
        }
        setEditing(false);
    };

    return (
        <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: 'minmax(0, 1fr) auto' }}
            gap={1.25}
            alignItems="center"
            sx={{
                border: `1px solid ${BORDER}`,
                bgcolor: 'var(--lm-surface)',
                borderRadius: CARD_RADIUS,
                px: 1.5,
                py: 1.25,
            }}
        >
            <Box minWidth={0}>
                {editing ? (
                    <TextField
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                save();
                            }
                        }}
                        multiline
                        minRows={2}
                        fullWidth
                        autoFocus
                    />
                ) : (
                    <>
                        <Typography sx={{ fontSize: 15, fontWeight: 650, lineHeight: 1.42 }}>{item.text}</Typography>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={0.75}
                            mt={0.75}
                        >
                            {item.type && (
                                <Chip
                                    size="small"
                                    label={formatKnowledgeItemType(item.type)}
                                    sx={{ height: 22, fontSize: 11, bgcolor: 'var(--lm-canvas)', fontWeight: 700 }}
                                />
                            )}
                            {item.sourceUrl && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'var(--lm-muted)' }}
                                >
                                    Источник: {item.sourceUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </Typography>
                            )}
                        </Box>
                    </>
                )}
            </Box>
            <Box
                display="flex"
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                gap={0.5}
            >
                {editing ? (
                    <>
                        <Tooltip title="Сохранить">
                            <IconButton
                                size="small"
                                onClick={save}
                            >
                                <IconCheck size={18} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Отменить">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setDraft(item.text);
                                    setEditing(false);
                                }}
                            >
                                <IconX size={18} />
                            </IconButton>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <Tooltip title="Редактировать">
                            <IconButton
                                size="small"
                                onClick={() => setEditing(true)}
                            >
                                <IconPencil size={18} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                            <IconButton
                                size="small"
                                onClick={onDelete}
                            >
                                <IconTrash size={18} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>
        </Box>
    );
}

function KnowledgeSectionEditor({
    section,
    emptyText,
    onAdd,
    onDelete,
    onChange,
}: {
    section: AIManagerDisplayKnowledgeSection;
    emptyText: string;
    onAdd: () => void;
    onDelete: (itemId: string) => void;
    onChange: (itemId: string, value: string) => void;
}) {
    const itemCount = section.items.filter((item) => item.text.trim()).length;

    return (
        <Box
            sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: CARD_RADIUS,
                bgcolor: 'var(--lm-surface)',
                p: 1.25,
            }}
        >
            <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '178px minmax(0, 1fr)' }}
                gap={1.25}
                alignItems="start"
            >
                <Box minWidth={0}>
                    <Typography sx={{ fontWeight: 900, lineHeight: 1.25 }}>{section.title}</Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--lm-muted)', mt: 0.4, display: 'block' }}
                    >
                        {itemCount ? `Тезисов: ${itemCount}` : 'Пока пусто'}
                    </Typography>
                </Box>
                <Stack
                    spacing={1}
                    minWidth={0}
                >
                    {section.items.map((item) => (
                        <KnowledgeItemRow
                            key={item.id}
                            item={item}
                            onDelete={() => onDelete(item.id)}
                            onChange={(value) => onChange(item.id, value)}
                        />
                    ))}
                    {!section.items.length && (
                        <Box
                            sx={{
                                border: `1px dashed ${BORDER}`,
                                borderRadius: '10px',
                                color: 'var(--lm-muted)',
                                fontSize: 14,
                                px: 1.25,
                                py: 1,
                            }}
                        >
                            {emptyText}
                        </Box>
                    )}
                    <Button
                        type="button"
                        size="small"
                        variant="text"
                        startIcon={<IconPlus size={18} />}
                        onClick={onAdd}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 750 }}
                    >
                        Добавить тезис
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}

function FieldProgressPanel({ progress }: { progress: AIManagerFieldProgress }) {
    return (
        <Box
            sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: CARD_RADIUS,
                p: 1.75,
                bgcolor: 'var(--lm-canvas)',
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                mb={1.25}
            >
                <Typography sx={{ fontWeight: 850 }}>Что уже выяснили</Typography>
                <Chip
                    size="small"
                    label={`${progress.collectedRequiredCount}/${progress.requiredCount}`}
                    sx={{
                        bgcolor: progress.complete ? 'var(--lm-success-bg)' : 'var(--lm-accent-soft)',
                        color: progress.complete ? 'var(--lm-success-ink)' : 'var(--lm-accent-ink)',
                    }}
                />
            </Box>
            <Stack spacing={1}>
                {progress.fields.map((field) => (
                    <Box
                        key={field.id}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: field.collected
                                    ? 'var(--lm-success-fill)'
                                    : field.missing
                                    ? 'var(--lm-danger-bg)'
                                    : 'var(--lm-border)',
                                color: field.collected
                                    ? 'var(--lm-accent-contrast)'
                                    : field.missing
                                    ? 'var(--lm-danger-ink)'
                                    : 'var(--lm-muted)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {field.collected ? <IconCheck size={14} /> : null}
                        </Box>
                        <Box minWidth={0}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                                flexWrap="wrap"
                            >
                                <Typography sx={{ fontSize: 14, fontWeight: field.required ? 750 : 500 }}>
                                    {field.label}
                                </Typography>
                                {!field.required && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'var(--lm-muted)' }}
                                    >
                                        опционально
                                    </Typography>
                                )}
                            </Box>
                            {field.collected && hasProgressValue(field.value) && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'var(--lm-muted)',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        lineHeight: 1.35,
                                    }}
                                >
                                    {formatProgressValue(field.value)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

function CompactScenarioBlock({ title, children }: { title: string; children: ReactNode }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: CARD_RADIUS,
                bgcolor: 'var(--lm-surface)',
                overflow: 'hidden',
            }}
        >
            <Button
                type="button"
                fullWidth
                onClick={() => setOpen((value) => !value)}
                sx={{
                    justifyContent: 'space-between',
                    color: 'var(--lm-text)',
                    textTransform: 'none',
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 0,
                }}
                endIcon={
                    <IconChevronDown
                        size={18}
                        style={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 150ms ease',
                        }}
                    />
                }
            >
                <Typography sx={{ fontWeight: 850 }}>{title}</Typography>
            </Button>
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 1.5,
                        pb: 1.5,
                        color: 'var(--lm-muted)',
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

export default function AIManagerSetupScreen() {
    const { managerId } = useParams<RouteParams>();
    const accountId = useUserStore((state) => state.id);
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [activeStep, setActiveStep] = useState<SetupStep>(() =>
        normalizeSetupStep(new URLSearchParams(location.search).get('step'))
    );
    const [streams, setStreams] = useState<Stream[]>([]);
    const [manager, setManager] = useState<AIManager | null>(null);
    const [resumeDraft, setResumeDraft] = useState<AIManager | null>(null);
    const [knowledgeDocument, setKnowledgeDocument] = useState<AIManagerKnowledgeDocument | null>(null);
    const [selectedKnowledgeDocumentId, setSelectedKnowledgeDocumentId] = useState<number | null>(null);
    const [managerNameDraft, setManagerNameDraft] = useState('');
    const [selectedStreamIds, setSelectedStreamIds] = useState<number[]>([]);
    const [goal, setGoal] = useState<AIManagerGoal>('qualify');
    const [goalChecklist, setGoalChecklist] = useState<ManagerGoalId[]>(DEFAULT_MANAGER_GOALS);
    const [fields, setFields] = useState<AIManagerQualificationField[]>(DEFAULT_FIELDS.qualify);
    const [dialogStyle, setDialogStyle] = useState<DialogStyle>('expert');
    const [scriptTemplate, setScriptTemplate] = useState<ScriptTemplateId>('sales_qualification');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [offerNotes, setOfferNotes] = useState('');
    const [knowledgeMode, setKnowledgeMode] = useState<KnowledgeMode>('website');
    const [knowledgeAnalysis, setKnowledgeAnalysis] = useState<AIManagerKnowledgeAnalysis | null>(null);
    const [displayProfile, setDisplayProfile] = useState<AIManagerDisplayKnowledgeProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const knowledgeRequest = useKnowledgeAnalysis(
        `${managerId || 'new'}:${location.search}:${knowledgeMode}`,
        getApiErrorMessage,
        displayProfile
    );
    const { pending: analyzing, error: knowledgeAnalysisError, setError: setKnowledgeAnalysisError } = knowledgeRequest;
    const [testLoading, setTestLoading] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [loadAttempt, setLoadAttempt] = useState(0);
    const setupScope = `${accountId}:${managerId || 'new'}`;
    const [loadedScope, setLoadedScope] = useState('');
    const knowledgeLibrary = useKnowledgeLibrary(
        setupScope,
        !loading && !errorText && accountId > 0 && loadedScope === setupScope
    );
    const availableKnowledgeDocuments = knowledgeLibrary.documents || [];
    const [testInput, setTestInput] = useState('Здравствуйте! Ищу решение для привлечения клиентов из Telegram');
    const [testMessages, setTestMessages] = useState<
        Array<{ id: string; role: 'user' | 'assistant'; content: string }>
    >([]);
    const [testSessionId, setTestSessionId] = useState<number | null>(null);
    const [testSessionState, setTestSessionState] = useState<any>(null);
    const [fieldProgress, setFieldProgress] = useState<AIManagerFieldProgress | null>(null);
    const [testRating, setTestRating] = useState<number | null>(null);
    const [testImprovement, setTestImprovement] = useState('');
    useEffect(() => {
        setActiveStep(normalizeSetupStep(new URLSearchParams(location.search).get('step')));
    }, [location.search]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setErrorText('');
            setKnowledgeDocument(null);
            setSelectedKnowledgeDocumentId(null);
            setKnowledgeAnalysis(null);
            try {
                const [loadedStreams, loadedManagers] = await Promise.all([
                    LeadStreamsService.getStreams(),
                    AIManagersService.getManagers(),
                ]);
                if (cancelled) {
                    return;
                }
                const paidStreams = loadedStreams.filter(hasPaidStreamHistory);
                setStreams(paidStreams);

                if (managerId) {
                    let loadedManager: AIManager;
                    try {
                        loadedManager = await AIManagersService.getManager(managerId);
                    } catch (error) {
                        if (!cancelled && getHttpStatus(error) === 404) {
                            enqueueSnackbar('Черновик уже недоступен. Открыли создание нового менеджера', {
                                variant: 'info',
                            });
                            history.replace(ROUTES_URL.AI_MANAGER_NEW);
                            return;
                        }
                        throw error;
                    }
                    if (cancelled) {
                        return;
                    }
                    setManager(loadedManager);
                    hydrateFromManager(loadedManager, paidStreams);
                } else {
                    const draft = loadedManagers.find(isSetupV2Draft) || null;
                    setResumeDraft(draft);
                    restoreLocalDraft(paidStreams);
                }
                setLoadedScope(setupScope);
            } catch (_error) {
                if (!cancelled) {
                    setErrorText('Не удалось загрузить настройку менеджера');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [enqueueSnackbar, managerId, loadAttempt, accountId, setupScope]);

    useEffect(() => {
        if (managerId || loading) {
            return;
        }
        window.localStorage.setItem(
            LOCAL_SETUP_DRAFT_KEY,
            JSON.stringify(
                serializeLocalDraft({
                    managerName: managerNameDraft,
                    selectedStreamIds,
                    goal,
                    fields,
                    goalChecklist,
                    dialogStyle,
                    scriptTemplate,
                    websiteUrl,
                    offerNotes,
                    knowledgeMode,
                    displayProfile,
                    selectedKnowledgeDocumentId,
                    testRating,
                    testImprovement,
                })
            )
        );
    }, [
        managerId,
        loading,
        managerNameDraft,
        selectedStreamIds,
        goal,
        fields,
        goalChecklist,
        dialogStyle,
        scriptTemplate,
        websiteUrl,
        offerNotes,
        knowledgeMode,
        displayProfile,
        selectedKnowledgeDocumentId,
        testRating,
        testImprovement,
    ]);

    const managerName = useMemo(() => {
        return managerNameDraft.trim() || manager?.name || '';
    }, [manager?.name, managerNameDraft]);

    const generatedNameHint = useMemo(() => {
        return generateManagerName(streams, selectedStreamIds);
    }, [selectedStreamIds, streams]);

    const selectedStreams = useMemo(
        () => streams.filter((stream) => selectedStreamIds.includes(stream.id)),
        [selectedStreamIds, streams]
    );
    const currentTemplateOptions = SCRIPT_TEMPLATES[goal];
    const currentTemplate =
        currentTemplateOptions.find((item) => item.id === scriptTemplate) || currentTemplateOptions[0];
    const testUserMessageCount = testMessages.filter((message) => message.role === 'user').length;
    const testMessageLimitReached = testUserMessageCount >= TEST_MAX_MESSAGES;
    const hasManagerName = Boolean(managerName);
    const hasSelectedStreams =
        selectedStreamIds.length > 0 ||
        Boolean(manager?.leadStreamIds.length || manager?.leadStreams.length || manager?.leadStreamNames.length);
    const shouldShowStreams = hasManagerName;
    const hasTestAnswer = testMessages.some((message) => message.role === 'assistant');
    const canCreateManager =
        hasTestAnswer && testSessionId !== null && testRating !== null && testRating >= 8;
    const setupCompleted =
        manager?.metadata.setup_completed === true ||
        manager?.metadata.setupCompleted === true ||
        manager?.status === 'ready_to_launch';

    const completed = useMemo(
        () => ({
            name: hasManagerName && hasSelectedStreams,
            knowledge: hasKnowledgeItems(displayProfile),
            scenario: fields.some((field) => field.required) && Boolean(scriptTemplate),
            test: setupCompleted || canCreateManager,
        }),
        [canCreateManager, displayProfile, fields, hasManagerName, hasSelectedStreams, scriptTemplate, setupCompleted]
    );

    const creationChecklist = useMemo<CreationChecklistItem[]>(
        () => [
            { label: 'Потоки выбраны', done: hasSelectedStreams },
            { label: 'Цель задана', done: Boolean(goal) },
            { label: 'Факты подготовлены', done: completed.knowledge },
            { label: 'Сценарий настроен', done: completed.scenario },
            { label: 'Telegram подключим после создания', done: true },
        ],
        [completed.knowledge, completed.scenario, goal, hasSelectedStreams]
    );

    const fallbackProgress = useMemo(() => {
        return buildFieldProgressFromLocal(fields, testSessionState?.knownFacts || {});
    }, [fields, testSessionState]);
    const displayedFieldProgress = useMemo(() => {
        const baseProgress = isFieldProgressForFields(fieldProgress, fields) ? fieldProgress : fallbackProgress;
        return mergeFieldProgressWithDialog(baseProgress, fields, testSessionState?.knownFacts || {}, testMessages);
    }, [fallbackProgress, fieldProgress, fields, testMessages, testSessionState]);

    function hydrateFromManager(loadedManager: AIManager, paidStreams: Stream[]) {
        const metadata = loadedManager.metadata || {};
        const paidStreamIds = new Set(paidStreams.map((stream) => stream.id));
        setManagerNameDraft(loadedManager.name || '');
        setSelectedStreamIds(
            paidStreams.length
                ? loadedManager.leadStreamIds.filter((streamId) => paidStreamIds.has(streamId))
                : loadedManager.leadStreamIds
        );
        setGoal(loadedManager.goal);
        setGoalChecklist(normalizeManagerGoals(metadata.goal_checklist));
        setFields(
            loadedManager.qualificationFields.length
                ? loadedManager.qualificationFields
                : DEFAULT_FIELDS[loadedManager.goal]
        );
        setWebsiteUrl(loadedManager.knowledgeSourceUrl || loadedManager.knowledgeSource || '');
        setOfferNotes(loadedManager.offerNotes || '');
        setDisplayProfile(
            hasKnowledgeItems(loadedManager.knowledgeDisplayProfile) ? loadedManager.knowledgeDisplayProfile : null
        );
        setFieldProgress(buildFieldProgressFromLocal(loadedManager.qualificationFields, {}));
        if (metadata.dialog_style === 'friendly' || metadata.dialog_style === 'direct') {
            setDialogStyle(metadata.dialog_style);
        }
        const storedTemplate = String(metadata.script_template || metadata.scriptTemplate || '').trim();
        if (storedTemplate && SCRIPT_TEMPLATES[loadedManager.goal].some((template) => template.id === storedTemplate)) {
            setScriptTemplate(storedTemplate as ScriptTemplateId);
        } else {
            setScriptTemplate(SCRIPT_TEMPLATES[loadedManager.goal][0].id);
        }
        const storedKnowledgeDocumentId = Number(metadata.knowledge_document_id || metadata.knowledgeDocumentId || 0);
        if (Number.isFinite(storedKnowledgeDocumentId) && storedKnowledgeDocumentId > 0) {
            setSelectedKnowledgeDocumentId(storedKnowledgeDocumentId);
            setKnowledgeMode('existing');
        } else {
            setKnowledgeMode('website');
        }
        const storedRating = Number(metadata.test_rating || metadata.testRating || 0);
        if (Number.isFinite(storedRating) && storedRating > 0) {
            setTestRating(storedRating);
        }
        setTestImprovement(String(metadata.test_improvement || metadata.testImprovement || ''));
    }

    function restoreLocalDraft(loadedStreams: Stream[]) {
        const paidStreamIds = new Set(loadedStreams.map((stream) => stream.id));
        const raw = window.localStorage.getItem(LOCAL_SETUP_DRAFT_KEY);
        if (!raw) {
            const queryStreamId = Number(new URLSearchParams(location.search).get('streamId') || 0);
            if (queryStreamId && paidStreamIds.has(queryStreamId)) {
                setSelectedStreamIds([queryStreamId]);
            }
            return;
        }
        try {
            const parsed = JSON.parse(raw);
            setGoalChecklist(normalizeManagerGoals(parsed.goalChecklist));
            setManagerNameDraft(String(parsed.managerName || ''));
            if (Array.isArray(parsed.selectedStreamIds)) {
                setSelectedStreamIds(
                    parsed.selectedStreamIds
                        .map(Number)
                        .filter((streamId: number) => Number.isFinite(streamId) && paidStreamIds.has(streamId))
                );
            }
            if (parsed.goal === 'recommend_service' || parsed.goal === 'qualify') {
                setGoal(parsed.goal);
                setFields(
                    Array.isArray(parsed.fields) && parsed.fields.length ? parsed.fields : DEFAULT_FIELDS[parsed.goal]
                );
            }
            if (
                parsed.dialogStyle === 'friendly' ||
                parsed.dialogStyle === 'direct' ||
                parsed.dialogStyle === 'expert'
            ) {
                setDialogStyle(parsed.dialogStyle);
            }
            const restoredGoal =
                parsed.goal === 'recommend_service' || parsed.goal === 'qualify' ? parsed.goal : 'qualify';
            const restoredTemplate = String(parsed.scriptTemplate || '').trim();
            if (SCRIPT_TEMPLATES[restoredGoal].some((template) => template.id === restoredTemplate)) {
                setScriptTemplate(restoredTemplate as ScriptTemplateId);
            } else {
                setScriptTemplate(SCRIPT_TEMPLATES[restoredGoal][0].id);
            }
            setWebsiteUrl(String(parsed.websiteUrl || ''));
            setOfferNotes(String(parsed.offerNotes || ''));
            if (parsed.knowledgeMode === 'existing' || parsed.knowledgeMode === 'website') {
                setKnowledgeMode(parsed.knowledgeMode);
            }
            if (parsed.displayProfile) {
                setDisplayProfile(withCompleteSections(parsed.displayProfile));
            }
            const draftKnowledgeDocumentId = Number(parsed.selectedKnowledgeDocumentId || 0);
            if (Number.isFinite(draftKnowledgeDocumentId) && draftKnowledgeDocumentId > 0) {
                setSelectedKnowledgeDocumentId(draftKnowledgeDocumentId);
                setKnowledgeMode('existing');
            }
            const draftRating = Number(parsed.testRating || 0);
            if (Number.isFinite(draftRating) && draftRating > 0) {
                setTestRating(draftRating);
            }
            setTestImprovement(String(parsed.testImprovement || ''));
        } catch (_error) {
            window.localStorage.removeItem(LOCAL_SETUP_DRAFT_KEY);
        }
    }

    const patchDisplayProfile = useCallback(
        (updater: (profile: AIManagerDisplayKnowledgeProfile) => AIManagerDisplayKnowledgeProfile) => {
            setDisplayProfile((current) => updater(withCompleteSections(current)));
        },
        []
    );

    const handleGoalChange = (nextGoal: AIManagerGoal) => {
        setGoal(nextGoal);
        setGoalChecklist([...DEFAULT_MANAGER_GOALS]);
        setFields(DEFAULT_FIELDS[nextGoal]);
        setScriptTemplate(SCRIPT_TEMPLATES[nextGoal][0].id);
        setTestMessages([]);
        setTestSessionId(null);
        setTestSessionState(null);
        setFieldProgress(null);
        setTestRating(null);
    };

    const toggleStream = (streamId: number) => {
        if (!streams.some((stream) => stream.id === streamId)) {
            return;
        }
        setSelectedStreamIds((current) =>
            current.includes(streamId) ? current.filter((id) => id !== streamId) : [...current, streamId]
        );
    };

    const updateField = (fieldId: string, patch: Partial<AIManagerQualificationField>) => {
        setFields((current) => current.map((field) => (field.id === fieldId ? { ...field, ...patch } : field)));
    };

    const addField = () => {
        const index = fields.length + 1;
        setFields((current) => [
            ...current,
            {
                id: `custom-${Date.now()}`,
                label: `Поле ${index}`,
                question: '',
                required: false,
            },
        ]);
    };

    const removeField = (fieldId: string) => {
        setFields((current) => current.filter((field) => field.id !== fieldId));
    };

    const saveSetupMetadata = async (id: string, patch: Record<string, unknown>) => {
        return AIManagersService.updateManager(id, {
            metadata: {
                ...patch,
                goal_checklist: goalChecklist,
                setup_version: 2,
                setup_updated_at: new Date().toISOString(),
                source: 'leadsmania2',
                entry_point: 'ai_managers_setup_v2',
            },
        });
    };

    const ensureDraftManager = async (step: SetupStep) => {
        if (manager) {
            const updated = await AIManagersService.updateManager(manager.id, {
                name: managerNameDraft.trim(),
                leadStreamIds: selectedStreamIds,
                goal,
                goalDescription: GOAL_DESCRIPTIONS[goal],
                flowSteps: currentTemplate.flowSteps,
                dataToCollect: fields.map((field) => field.label),
                firstMessage: currentTemplate.openingMessage,
                handoffRule: currentTemplate.handoffRule,
                stopPhrases: STOP_PHRASES,
                llmModel: TEST_MODEL,
                llmTemperature: 0.4,
                llmTopP: 1,
                llmMaxOutputTokens: TEST_MAX_OUTPUT_TOKENS,
                maxMessagesPerDialog: TEST_MAX_MESSAGES,
                followUps: DEFAULT_FOLLOW_UPS,
                status: manager.status === 'running' ? manager.status : 'draft',
                metadata: {
                    setup_version: 2,
                    setup_step: step,
                    setup_updated_at: new Date().toISOString(),
                    qualification_fields: fields,
                    goal_checklist: goalChecklist,
                    dialog_style: dialogStyle,
                    script_template: scriptTemplate,
                    script_template_title: currentTemplate.title,
                    knowledge_mode: knowledgeMode,
                    test_model: TEST_MODEL,
                    test_max_output_tokens: TEST_MAX_OUTPUT_TOKENS,
                    follow_up_rules: DEFAULT_FOLLOW_UPS,
                    defaults_version: '2026-08-05',
                    entry_point: 'ai_managers_setup_v2',
                    source: 'leadsmania2',
                },
            });
            setManager(updated);
            return updated;
        }

        const created = await AIManagersService.createManager({
            name: managerNameDraft.trim(),
            leadStreamIds: selectedStreamIds,
            leadStreamNames: selectedStreams.map(selectedStreamName),
            goal,
            goalDescription: GOAL_DESCRIPTIONS[goal],
            flowSteps: currentTemplate.flowSteps,
            dataToCollect: fields.map((field) => field.label),
            openingMessages: [currentTemplate.openingMessage],
            firstMessage: currentTemplate.openingMessage,
            handoffRule: currentTemplate.handoffRule,
            stopPhrases: STOP_PHRASES,
            avatarPaletteId: 'aurora',
            qualificationFields: fields,
            tone: dialogStyle,
            llmModel: TEST_MODEL,
            llmTemperature: 0.4,
            llmTopP: 1,
            llmMaxOutputTokens: TEST_MAX_OUTPUT_TOKENS,
            maxMessagesPerDialog: TEST_MAX_MESSAGES,
            offerNotes,
            knowledgeSource: websiteUrl,
            knowledgeSourceUrl: normalizeKnowledgeSourceUrl(knowledgeAnalysis?.sourceUrl || websiteUrl),
            knowledgeFacts: displayProfile
                ? buildKnowledgeFactsFromProfile(withCompleteSections(displayProfile))
                : null,
            knowledgeDisplayProfile: displayProfile,
            followUps: DEFAULT_FOLLOW_UPS,
            status: 'draft',
            metadata: {
                setup_version: 2,
                setup_step: step,
                setup_started_at: new Date().toISOString(),
                goal_checklist: goalChecklist,
                setup_updated_at: new Date().toISOString(),
                dialog_style: dialogStyle,
                script_template: scriptTemplate,
                script_template_title: currentTemplate.title,
                knowledge_mode: knowledgeMode,
                test_model: TEST_MODEL,
                test_max_output_tokens: TEST_MAX_OUTPUT_TOKENS,
                follow_up_rules: DEFAULT_FOLLOW_UPS,
                defaults_version: '2026-08-05',
                entry_point: 'ai_managers_setup_v2',
                source: 'leadsmania2',
            },
        });
        setManager(created);
        return created;
    };

    const goToStep = (id: string, step: SetupStep) => {
        setActiveStep(step);
        history.replace(`/ai-managers/${id}/setup?step=${step}`);
    };

    const handleContinueName = async () => {
        if (!managerNameDraft.trim()) {
            enqueueSnackbar('Укажите имя менеджера', { variant: 'info' });
            return;
        }
        if (!selectedStreamIds.length) {
            enqueueSnackbar('Выберите хотя бы один поток', { variant: 'info' });
            return;
        }
        setSaving(true);
        try {
            const draft = await ensureDraftManager('knowledge');
            window.localStorage.removeItem(LOCAL_SETUP_DRAFT_KEY);
            goToStep(draft.id, 'knowledge');
        } catch (_error) {
            enqueueSnackbar('Не удалось сохранить черновик', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleAnalyzeWebsite = async () => {
        if (analyzing || saving) return;
        if (!websiteUrl.trim() && !offerNotes.trim()) {
            enqueueSnackbar('Укажите сайт или короткое описание', { variant: 'info' });
            return;
        }
        setErrorText('');
        if (!websiteUrl.trim()) {
            knowledgeRequest.reset();
            const profile = createEmptyDisplayProfile();
            profile.sections[0].items.push({
                id: `manual-${Date.now()}`,
                text: offerNotes.trim(),
                origin: 'manual',
                type: 'source_note',
            });
            setKnowledgeAnalysis(null);
            setKnowledgeDocument(null);
            setSelectedKnowledgeDocumentId(null);
            setDisplayProfile(withCompleteSections(profile));
            enqueueSnackbar('Описание добавлено в черновик', { variant: 'success' });
            return;
        }
        await knowledgeRequest.run({ url: websiteUrl, offerNotes }, (analysis) => {
            setKnowledgeAnalysis(analysis);
            setKnowledgeDocument(null);
            setSelectedKnowledgeDocumentId(null);
            setDisplayProfile(withCompleteSections(analysis.displayProfile));
            enqueueSnackbar('Факты собраны. Проверьте их перед сохранением', { variant: 'success' });
        });
    };

    const handleSelectKnowledgeDocument = (document: AIManagerKnowledgeDocument) => {
        setKnowledgeMode('existing');
        setKnowledgeAnalysisError('');
        knowledgeRequest.reset();
        setKnowledgeAnalysis(null);
        setKnowledgeDocument(document);
        setSelectedKnowledgeDocumentId(document.id);
        setWebsiteUrl(document.sourceUrl || websiteUrl);
        setDisplayProfile(normalizeDisplayProfileFromDocument(document));
        const offerNote = String(document.metadata.offer_notes || document.metadata.offerNotes || '').trim();
        if (offerNote) {
            setOfferNotes(offerNote);
        }
        enqueueSnackbar('Готовая база знаний выбрана', { variant: 'success' });
    };

    const upsertKnowledgeDocument = async (draft: AIManager, profile: AIManagerDisplayKnowledgeProfile) => {
        const body = buildKnowledgeBody(profile, offerNotes);
        const sourceUrl = normalizeKnowledgeSourceUrl(knowledgeAnalysis?.sourceUrl || websiteUrl);
        const summary = body.slice(0, 1800);
        const metadata = {
            source: knowledgeMode === 'existing' ? 'leadsmania2_existing_knowledge' : 'leadsmania2_website_analysis',
            setup_version: 2,
            knowledge_mode: knowledgeMode,
            approved_profile: toBackendDisplayProfile(profile),
            raw_facts: knowledgeAnalysis?.facts || {},
            pages: knowledgeAnalysis?.pages || [],
            offer_notes: offerNotes.trim(),
            source_knowledge_document_id: selectedKnowledgeDocumentId,
            analyzed_at: new Date().toISOString(),
        };
        const docs = await AIManagersService.listKnowledgeDocuments(draft.id);
        const existing =
            docs.find((doc) => doc.documentType === 'company_profile' || doc.tags.includes('setup-v2')) || null;
        const payload = {
            agentProfile: draft.id,
            title: 'Профиль компании',
            summary,
            body,
            scope: 'profile',
            documentType: 'company_profile',
            priority: 90,
            sourceUrl,
            tags: ['leadsmania2', 'website', 'offer', 'setup-v2'],
            metadata,
            isActive: true,
        };
        const saved = existing
            ? await AIManagersService.updateKnowledgeDocument(existing.id, payload)
            : await AIManagersService.createKnowledgeDocument(payload);
        setKnowledgeDocument(saved);
        return saved;
    };

    const handleContinueKnowledge = async () => {
        if (analyzing) return;
        const profile = withCompleteSections(displayProfile);
        if (!hasKnowledgeItems(profile)) {
            enqueueSnackbar('Сначала соберите или добавьте базу знаний', { variant: 'info' });
            return;
        }
        setSaving(true);
        try {
            const draft = await ensureDraftManager('knowledge');
            const body = buildKnowledgeBody(profile, offerNotes);
            const sourceUrl = normalizeKnowledgeSourceUrl(knowledgeAnalysis?.sourceUrl || websiteUrl);
            const approvedKnowledgeFacts = buildKnowledgeFactsFromProfile(profile);
            const savedDocument = await upsertKnowledgeDocument(draft, profile);
            const updated = await saveSetupMetadata(draft.id, {
                setup_step: 'scenario',
                knowledge_mode: knowledgeMode,
                offer_notes: offerNotes.trim(),
                knowledge_source: websiteUrl.trim(),
                knowledge_source_url: sourceUrl,
                knowledge_summary: body.slice(0, 1800),
                knowledge_body: body,
                knowledge_facts: approvedKnowledgeFacts,
                knowledge_pages: knowledgeAnalysis?.pages || draft.knowledgePages || [],
                approved_profile: toBackendDisplayProfile(profile),
                knowledge_display_profile: toBackendDisplayProfile(profile),
                knowledge_document_id: savedDocument.id,
                qualification_fields: fields,
            });
            setManager(updated);
            goToStep(draft.id, 'scenario');
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, 'Не удалось сохранить базу знаний'), { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleContinueScenario = async () => {
        if (goal === 'qualify' && goalChecklist.length === 0) {
            enqueueSnackbar('Выберите хотя бы одну цель разговора', { variant: 'info' });
            return;
        }
        if (!fields.some((field) => field.required)) {
            enqueueSnackbar('Оставьте хотя бы одно обязательное поле', { variant: 'info' });
            return;
        }
        if (!scriptTemplate) {
            enqueueSnackbar('Выберите шаблон сценария', { variant: 'info' });
            return;
        }
        setTestMessages([]);
        setTestSessionId(null);
        setTestSessionState(null);
        setFieldProgress(null);
        setTestRating(null);
        setSaving(true);
        try {
            const draft = await ensureDraftManager('test');
            const updated = await saveSetupMetadata(draft.id, {
                setup_step: 'test',
                goal,
                goal_description: GOAL_DESCRIPTIONS[goal],
                dialog_style: dialogStyle,
                script_template: scriptTemplate,
                script_template_title: currentTemplate.title,
                opening_message: currentTemplate.openingMessage,
                flow_steps: currentTemplate.flowSteps,
                handoff_rule: currentTemplate.handoffRule,
                qualification_fields: fields,
                follow_up_rules: DEFAULT_FOLLOW_UPS,
                test_model: TEST_MODEL,
                test_max_output_tokens: TEST_MAX_OUTPUT_TOKENS,
                test_max_messages: TEST_MAX_MESSAGES,
            });
            setManager(updated);
            goToStep(draft.id, 'test');
        } catch (_error) {
            enqueueSnackbar('Не удалось сохранить сценарий', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleSendTestMessage = async () => {
        const draft = manager;
        const content = testInput.trim();
        if (!draft || !content || testLoading) {
            return;
        }
        if (testMessageLimitReached) {
            enqueueSnackbar(`Тестовый диалог ограничен ${TEST_MAX_MESSAGES} сообщениями клиента`, { variant: 'info' });
            return;
        }
        setTestRating(null);
        const userMessage = { id: `user-${Date.now()}`, role: 'user' as const, content };
        const nextMessages = [...testMessages, userMessage];
        setTestMessages(nextMessages);
        setTestInput('');
        setTestLoading(true);
        try {
            const currentDraft = await ensureDraftManager('test');
            const result = await AIManagersService.testDialog({
                managerId: currentDraft.id,
                messages: nextMessages,
                sessionId: testSessionId,
                sessionState: testSessionState,
            });
            setTestMessages([
                ...nextMessages,
                {
                    id: result.assistantMessage.id || `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: result.assistantMessage.content,
                },
            ]);
            setTestSessionId(result.sessionId);
            setTestSessionState(result.sessionState);
            setFieldProgress(
                result.fieldProgress || buildFieldProgressFromLocal(fields, result.sessionState.knownFacts)
            );
            await saveSetupMetadata(draft.id, { setup_step: 'test', test_dialog_checked_at: new Date().toISOString() });
        } catch (error) {
            setTestMessages(testMessages);
            setTestInput(content);
            enqueueSnackbar(getApiErrorMessage(error, 'Модель не ответила вовремя. Попробуйте еще раз'), {
                variant: 'error',
            });
        } finally {
            setTestLoading(false);
        }
    };

    const handleRestartTest = () => {
        setTestMessages([]);
        setTestSessionId(null);
        setTestSessionState(null);
        setFieldProgress(null);
        setTestRating(null);
        setTestInput('Здравствуйте! Ищу решение для привлечения клиентов из Telegram');
    };

    const handleApplyTestImprovement = async () => {
        if (!manager || !testImprovement.trim()) {
            enqueueSnackbar('Напишите, что нужно улучшить', { variant: 'info' });
            return;
        }
        setSaving(true);
        try {
            const updated = await saveSetupMetadata(manager.id, {
                setup_step: 'test',
                test_improvement: testImprovement.trim(),
                test_rating: testRating,
                scenario_iteration_at: new Date().toISOString(),
            });
            setManager(updated);
            handleRestartTest();
            enqueueSnackbar('Уточнение сохранено. Проверьте диалог еще раз', { variant: 'success' });
        } catch (_error) {
            enqueueSnackbar('Не удалось сохранить уточнение', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCreateManager = async () => {
        if (!manager) {
            return;
        }
        if (!canCreateManager) {
            enqueueSnackbar('Сначала проверьте ответ и поставьте оценку 8 или выше', { variant: 'info' });
            return;
        }
        setSaving(true);
        try {
            await AIManagersService.acceptSetupTest({
                managerId: manager.id,
                sessionId: testSessionId as number,
                rating: testRating as number,
            });
            const updated = await AIManagersService.updateManager(manager.id, {
                status: 'ready_to_launch',
                metadata: {
                    setup_version: 2,
                    setup_step: 'test',
                    setup_completed: true,
                    setup_completed_at: new Date().toISOString(),
                    test_rating: testRating,
                    test_improvement: testImprovement.trim(),
                    test_model: TEST_MODEL,
                    test_max_output_tokens: TEST_MAX_OUTPUT_TOKENS,
                },
            });
            setManager(updated);
            EventService.dispatch('AI_MANAGERS_UPDATED', updated);
            window.localStorage.removeItem(LOCAL_SETUP_DRAFT_KEY);
            enqueueSnackbar('AI-менеджер создан', { variant: 'success' });
            history.push(`/ai-managers/${updated.id}`);
        } catch (_error) {
            enqueueSnackbar('Не удалось создать менеджера', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        const currentId = manager?.id || resumeDraft?.id;
        setSaving(true);
        try {
            if (currentId) {
                await AIManagersService.deleteManager(currentId);
            }
            window.localStorage.removeItem(LOCAL_SETUP_DRAFT_KEY);
            setManager(null);
            setResumeDraft(null);
            setKnowledgeDocument(null);
            setSelectedKnowledgeDocumentId(null);
            setManagerNameDraft('');
            setSelectedStreamIds([]);
            setGoal('qualify');
            setGoalChecklist([...DEFAULT_MANAGER_GOALS]);
            setFields(DEFAULT_FIELDS.qualify);
            setDialogStyle('expert');
            setScriptTemplate('sales_qualification');
            setWebsiteUrl('');
            setOfferNotes('');
            setKnowledgeMode('website');
            setKnowledgeAnalysis(null);
            knowledgeRequest.reset();
            setKnowledgeAnalysisError('');
            setDisplayProfile(null);
            setTestMessages([]);
            setTestSessionId(null);
            setTestSessionState(null);
            setFieldProgress(null);
            setTestRating(null);
            setTestImprovement('');
            setActiveStep('name');
            history.replace(ROUTES_URL.AI_MANAGER_NEW);
        } catch (_error) {
            enqueueSnackbar('Не удалось начать заново', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const addKnowledgeItem = (sectionKey: AIManagerKnowledgeSectionKey) => {
        patchDisplayProfile((profile) => ({
            ...profile,
            sections: profile.sections.map((section) =>
                section.key === sectionKey
                    ? {
                          ...section,
                          items: [
                              ...section.items,
                              {
                                  id: `manual-${sectionKey}-${Date.now()}`,
                                  text: '',
                                  origin: 'manual',
                                  type: 'manual',
                              },
                          ],
                      }
                    : section
            ),
        }));
    };

    const deleteKnowledgeItem = (sectionKey: AIManagerKnowledgeSectionKey, itemId: string) => {
        patchDisplayProfile((profile) => ({
            ...profile,
            sections: profile.sections.map((section) =>
                section.key === sectionKey
                    ? { ...section, items: section.items.filter((item) => item.id !== itemId) }
                    : section
            ),
        }));
    };

    const updateKnowledgeItem = (sectionKey: AIManagerKnowledgeSectionKey, itemId: string, value: string) => {
        patchDisplayProfile((profile) => ({
            ...profile,
            sections: profile.sections.map((section) =>
                section.key === sectionKey
                    ? {
                          ...section,
                          items: section.items.map((item) => (item.id === itemId ? { ...item, text: value } : item)),
                      }
                    : section
            ),
        }));
    };

    const renderNameStep = () => (
        <Stack spacing={3}>
            {resumeDraft && !manager && (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: CARD_RADIUS,
                        borderColor: 'var(--lm-accent-border)',
                        bgcolor: 'var(--lm-accent-soft)',
                        p: 2,
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        gap={1.5}
                        flexDirection={{ xs: 'column', md: 'row' }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 850 }}>Есть незавершенная настройка</Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: 'var(--lm-muted)' }}
                            >
                                {resumeDraft.name}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            gap={1}
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleReset}
                                disabled={saving}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 750 }}
                            >
                                Начать заново
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                onClick={() =>
                                    history.push(
                                        `/ai-managers/${resumeDraft.id}/setup?step=${getDraftStep(resumeDraft)}`
                                    )
                                }
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 850 }}
                            >
                                Продолжить
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 900 }}>Имя менеджера</Typography>
                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>Как будет представляться менеджер</Typography>
                <TextField
                    value={managerNameDraft}
                    onChange={(event) => setManagerNameDraft(event.target.value)}
                    placeholder={generatedNameHint || 'Например: Анна'}
                    fullWidth
                    sx={{ mt: 1.25 }}
                />
            </Box>

            {shouldShowStreams && (
                <>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 900 }}>Выберите потоки</Typography>
                        <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>
                            Доступны только купленные потоки
                        </Typography>
                    </Box>
                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
                        gap={1.25}
                    >
                        {!streams.length && (
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    border: `1px solid ${BORDER}`,
                                    borderRadius: CARD_RADIUS,
                                    bgcolor: 'var(--lm-canvas)',
                                    p: 2,
                                }}
                            >
                                <Typography sx={{ fontWeight: 850 }}>Купленных потоков пока нет</Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>
                                    Сначала активируйте поток, потом подключите к нему AI-менеджера
                                </Typography>
                            </Box>
                        )}
                        {streams.map((stream) => {
                            const selected = selectedStreamIds.includes(stream.id);
                            const streamName = selectedStreamName(stream);
                            return (
                                <Box
                                    key={stream.id}
                                    role="checkbox"
                                    tabIndex={0}
                                    aria-checked={selected}
                                    aria-label={streamName}
                                    onClick={() => toggleStream(stream.id)}
                                    onKeyDown={handleKeyboardActivation(() => toggleStream(stream.id))}
                                    sx={{
                                        cursor: 'pointer',
                                        border: `1px solid ${selected ? 'var(--lm-accent-ink)' : BORDER}`,
                                        borderRadius: CARD_RADIUS,
                                        bgcolor: selected ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                                        px: 1.5,
                                        py: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Checkbox
                                        checked={selected}
                                        tabIndex={-1}
                                        sx={{ p: 0.25 }}
                                    />
                                    <Box minWidth={0}>
                                        <Typography sx={{ fontWeight: 800 }}>{streamName}</Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'var(--lm-muted)' }}
                                        >
                                            {getStreamAccessLabel(stream) || stream.category?.name || 'Купленный поток'}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Stack>
    );

    const renderKnowledgeStep = () => {
        const profile = withCompleteSections(displayProfile);
        const knowledgeReady = hasKnowledgeItems(profile);
        const knowledgeItemCount = getKnowledgeItemCount(profile);
        const selectedKnowledgeDocument =
            availableKnowledgeDocuments.find((document) => document.id === selectedKnowledgeDocumentId) ||
            (knowledgeDocument?.id === selectedKnowledgeDocumentId ? knowledgeDocument : null);
        const displaySections = KNOWLEDGE_SECTIONS.map((section) => {
            return (
                profile.sections.find((item) => item.key === section.key) || {
                    key: section.key,
                    title: section.title,
                    items: [],
                }
            );
        });
        const populatedSections = displaySections.filter((section) => section.items.length > 0);
        const emptySections = KNOWLEDGE_SECTIONS.filter(
            (section) => !populatedSections.some((item) => item.key === section.key)
        );

        return (
            <Stack spacing={3}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 900 }}>Оффер и база знаний</Typography>
                    <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>
                        Выберите готовую базу или соберите факты из открытого сайта
                    </Typography>
                </Box>

                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
                    gap={1.25}
                >
                    <Box
                        role="button"
                        aria-pressed={knowledgeMode === 'existing'}
                        tabIndex={0}
                        onClick={() => {
                            knowledgeRequest.reset();
                            setKnowledgeMode('existing');
                            setKnowledgeAnalysisError('');
                        }}
                        onKeyDown={handleKeyboardActivation(() => {
                            knowledgeRequest.reset();
                            setKnowledgeMode('existing');
                            setKnowledgeAnalysisError('');
                        })}
                        sx={{
                            cursor: 'pointer',
                            border: `1px solid ${knowledgeMode === 'existing' ? 'var(--lm-accent-ink)' : BORDER}`,
                            borderRadius: CARD_RADIUS,
                            bgcolor: knowledgeMode === 'existing' ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                            p: 1.5,
                        }}
                    >
                        <Typography sx={{ fontWeight: 900 }}>Выбрать готовую</Typography>
                        <Typography sx={{ color: 'var(--lm-muted)', mt: 0.45, lineHeight: 1.45 }}>
                            {knowledgeLibrary.documents
                                ? `Доступно баз: ${availableKnowledgeDocuments.length}`
                                : 'Сохранённые профили компании'}
                        </Typography>
                    </Box>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            if (knowledgeMode !== 'website' && selectedKnowledgeDocumentId) {
                                setDisplayProfile(null);
                                setKnowledgeAnalysis(null);
                            }
                            setKnowledgeMode('website');
                            setSelectedKnowledgeDocumentId(null);
                            setKnowledgeDocument(null);
                        }}
                        onKeyDown={handleKeyboardActivation(() => {
                            if (knowledgeMode !== 'website' && selectedKnowledgeDocumentId) {
                                setDisplayProfile(null);
                                setKnowledgeAnalysis(null);
                            }
                            setKnowledgeMode('website');
                            setSelectedKnowledgeDocumentId(null);
                            setKnowledgeDocument(null);
                        })}
                        sx={{
                            cursor: 'pointer',
                            border: `1px solid ${knowledgeMode === 'website' ? 'var(--lm-accent-ink)' : BORDER}`,
                            borderRadius: CARD_RADIUS,
                            bgcolor: knowledgeMode === 'website' ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                            p: 1.5,
                        }}
                    >
                        <Typography sx={{ fontWeight: 900 }}>Сформировать из сайта</Typography>
                        <Typography sx={{ color: 'var(--lm-muted)', mt: 0.45, lineHeight: 1.45 }}>
                            Соберем УТП, клиентов, цены, ограничения и контакты
                        </Typography>
                    </Box>
                </Box>

                {(knowledgeMode === 'existing' || knowledgeLibrary.error) && (
                    <KnowledgeLibraryState
                        pending={knowledgeLibrary.pending}
                        error={knowledgeLibrary.error}
                        hasData={knowledgeLibrary.documents !== null}
                        onReload={knowledgeLibrary.reload}
                    />
                )}

                {knowledgeMode === 'existing' && (
                    <Box>
                        {availableKnowledgeDocuments.length > 0 ? (
                            <>
                                <Box
                                    display="flex"
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    justifyContent="space-between"
                                    gap={1}
                                    mb={1.25}
                                    flexDirection={{ xs: 'column', sm: 'row' }}
                                >
                                    <Box>
                                        <Typography sx={{ color: 'var(--lm-muted)' }}>
                                            Выберите профиль компании
                                        </Typography>
                                    </Box>
                                    {selectedKnowledgeDocument && (
                                        <Chip
                                            size="small"
                                            label={`Выбрано: ${formatKnowledgeSourceLabel(
                                                selectedKnowledgeDocument.sourceUrl
                                            )}`}
                                            sx={{
                                                bgcolor: 'var(--lm-accent-soft)',
                                                color: 'var(--lm-accent)',
                                                fontWeight: 800,
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box
                                    display="grid"
                                    gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
                                    gap={1.25}
                                >
                                    {availableKnowledgeDocuments.slice(0, 6).map((document) => {
                                        const selected = selectedKnowledgeDocumentId === document.id;
                                        const sourceLabel = formatKnowledgeSourceLabel(document.sourceUrl);
                                        const documentProfile = normalizeDisplayProfileFromDocument(document);
                                        const documentFactsCount = getKnowledgeItemCount(documentProfile);
                                        const updatedDate = formatCompactDate(document.updatedAt);
                                        const preview = getKnowledgeDocumentPreview(documentProfile, document.summary);
                                        return (
                                            <Box
                                                key={document.id}
                                                role="button"
                                                tabIndex={0}
                                                aria-pressed={selected}
                                                onClick={() => handleSelectKnowledgeDocument(document)}
                                                onKeyDown={handleKeyboardActivation(() =>
                                                    handleSelectKnowledgeDocument(document)
                                                )}
                                                sx={{
                                                    cursor: 'pointer',
                                                    border: `1px solid ${selected ? 'var(--lm-accent-ink)' : BORDER}`,
                                                    borderRadius: CARD_RADIUS,
                                                    bgcolor: selected ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                                                    p: 1.5,
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    gap={1}
                                                >
                                                    <Typography sx={{ fontWeight: 900 }}>
                                                        {document.title || 'Профиль компании'}
                                                    </Typography>
                                                    {selected && (
                                                        <IconCheck
                                                            size={18}
                                                            color="var(--lm-accent-ink)"
                                                        />
                                                    )}
                                                </Box>
                                                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.35, fontSize: 14 }}>
                                                    {sourceLabel}
                                                    {updatedDate ? ` · ${updatedDate}` : ''}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: 'var(--lm-accent-ink)',
                                                        mt: 0.55,
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                    }}
                                                >
                                                    Фактов: {documentFactsCount}
                                                </Typography>
                                                {preview && (
                                                    <Typography
                                                        sx={{
                                                            color: 'var(--lm-muted)',
                                                            mt: 0.75,
                                                            lineHeight: 1.45,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {preview}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </>
                        ) : knowledgeLibrary.documents && !knowledgeLibrary.pending && !knowledgeLibrary.error ? (
                            <Box
                                sx={{
                                    border: `1px dashed ${BORDER}`,
                                    borderRadius: CARD_RADIUS,
                                    bgcolor: 'var(--lm-canvas)',
                                    p: 2,
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }}>Готовых баз пока нет</Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>
                                    Соберите факты из сайта или добавьте описание вручную
                                </Typography>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() => setKnowledgeMode('website')}
                                    sx={{ mt: 1.25, borderRadius: '10px', textTransform: 'none', fontWeight: 800 }}
                                >
                                    Перейти к сайту
                                </Button>
                            </Box>
                        ) : null}
                    </Box>
                )}

                {knowledgeMode === 'website' && (
                    <Stack spacing={2}>
                        <Stack spacing={1}>
                            <Typography
                                component="label"
                                htmlFor="manager-knowledge-website"
                                sx={{ fontSize: 16, fontWeight: 850 }}
                            >
                                Сайт компании
                            </Typography>
                            <TextField
                                id="manager-knowledge-website"
                                value={websiteUrl}
                                onChange={(event) => {
                                    setKnowledgeAnalysisError('');
                                    setWebsiteUrl(event.target.value);
                                    setKnowledgeMode('website');
                                }}
                                placeholder="tghunter.ru"
                                fullWidth
                                disabled={analyzing}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <Typography
                                component="label"
                                htmlFor="manager-knowledge-notes"
                                sx={{ fontSize: 16, fontWeight: 850 }}
                            >
                                Что важно знать менеджеру
                            </Typography>
                            <TextField
                                id="manager-knowledge-notes"
                                value={offerNotes}
                                onChange={(event) => {
                                    setKnowledgeAnalysisError('');
                                    setOfferNotes(event.target.value);
                                    setKnowledgeMode('website');
                                }}
                                placeholder="Например: минимальный чек, кому не подходим, приоритетный продукт"
                                fullWidth
                                multiline
                                minRows={3}
                                disabled={analyzing}
                            />
                            <Typography sx={{ color: 'var(--lm-muted)' }}>
                                После анализа проверьте тезисы и удалите лишнее
                            </Typography>
                        </Stack>
                        <KnowledgeAnalysisState
                            pending={analyzing}
                            error={knowledgeAnalysisError}
                            onRetry={handleAnalyzeWebsite}
                        />
                    </Stack>
                )}

                {knowledgeReady && (
                    <Stack spacing={1.25}>
                        <Box
                            display="flex"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            gap={1}
                            flexDirection={{ xs: 'column', sm: 'row' }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>Факты для проверки</Typography>
                            </Box>
                            <Chip
                                size="small"
                                label={`Фактов: ${knowledgeItemCount}`}
                                sx={{ bgcolor: 'var(--lm-accent-soft)', color: 'var(--lm-accent)', fontWeight: 800 }}
                            />
                        </Box>
                        {profile.warnings.length > 0 && (
                            <Box
                                sx={{
                                    border: `1px solid var(--lm-warning-ink)`,
                                    bgcolor: 'var(--lm-warning-bg)',
                                    borderRadius: CARD_RADIUS,
                                    p: 1.25,
                                }}
                            >
                                <Typography sx={{ color: 'var(--lm-warning-ink)', fontWeight: 700 }}>
                                    {profile.warnings.join(' · ')}
                                </Typography>
                            </Box>
                        )}
                        {populatedSections.map((section) => {
                            const sectionMeta = KNOWLEDGE_SECTIONS.find((item) => item.key === section.key);
                            return (
                                <KnowledgeSectionEditor
                                    key={section.key}
                                    section={section}
                                    emptyText={sectionMeta?.empty || 'Добавьте тезис'}
                                    onAdd={() => addKnowledgeItem(section.key)}
                                    onDelete={(itemId) => deleteKnowledgeItem(section.key, itemId)}
                                    onChange={(itemId, value) => updateKnowledgeItem(section.key, itemId, value)}
                                />
                            );
                        })}
                        {emptySections.length > 0 && (
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={0.75}
                                pt={0.25}
                            >
                                {emptySections.map((section) => (
                                    <Box
                                        key={section.key}
                                        component="button"
                                        type="button"
                                        onClick={() => addKnowledgeItem(section.key)}
                                        sx={{
                                            cursor: 'pointer',
                                            border: `1px solid ${BORDER}`,
                                            borderRadius: '10px',
                                            bgcolor: 'var(--lm-surface)',
                                            color: 'var(--lm-muted)',
                                            font: 'inherit',
                                            fontSize: 13,
                                            fontWeight: 750,
                                            px: 1,
                                            py: 0.65,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <IconPlus size={15} />
                                        {section.title}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Stack>
                )}
            </Stack>
        );
    };

    const renderScenarioStep = () => (
        <Stack spacing={3}>
            <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 900 }}>Стиль, цель и сценарий</Typography>
                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>
                    Сначала выберите, какую задачу решает AI-менеджер, затем проверьте вопросы
                </Typography>
            </Box>

            <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 900, mb: 1.25 }}>Стиль общения</Typography>
                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
                    gap={1.25}
                >
                    {STYLE_OPTIONS.map((option) => {
                        const selected = dialogStyle === option.id;
                        return (
                            <Box
                                key={option.id}
                                onClick={() => setDialogStyle(option.id)}
                                sx={{
                                    cursor: 'pointer',
                                    border: `1px solid ${selected ? 'var(--lm-accent-ink)' : BORDER}`,
                                    borderRadius: CARD_RADIUS,
                                    bgcolor: selected ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                                    p: 1.5,
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }}>{option.title}</Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5, lineHeight: 1.45 }}>
                                    {option.description}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 900, mb: 1.25 }}>Выберите цель</Typography>
                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
                    gap={1.25}
                >
                    {(['qualify', 'recommend_service'] as AIManagerGoal[]).map((option) => {
                        const selected = goal === option;
                        return (
                            <Box
                                key={option}
                                onClick={() => handleGoalChange(option)}
                                sx={{
                                    cursor: 'pointer',
                                    border: `1px solid ${selected ? 'var(--lm-accent-ink)' : BORDER}`,
                                    borderRadius: CARD_RADIUS,
                                    bgcolor: selected ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                                    p: 1.75,
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }}>{AI_MANAGER_GOAL_LABELS[option]}</Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.75, lineHeight: 1.45 }}>
                                    {GOAL_DESCRIPTIONS[option]}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {goal === 'qualify' && <ManagerGoalChecklist value={goalChecklist} onChange={(nextGoals) => {
                setGoalChecklist(nextGoals);
                setFields((current) => fieldsForGoals(current, nextGoals));
                setTestMessages([]);
                setTestSessionId(null);
                setTestSessionState(null);
                setFieldProgress(null);
                setTestRating(null);
            }} />}

            <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 900, mb: 1.25 }}>Шаблон взаимодействия</Typography>
                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
                    gap={1.25}
                >
                    {currentTemplateOptions.map((template) => {
                        const selected = scriptTemplate === template.id;
                        return (
                            <Box
                                key={template.id}
                                onClick={() => {
                                    setScriptTemplate(template.id);
                                    setTestMessages([]);
                                    setTestSessionId(null);
                                    setTestSessionState(null);
                                    setFieldProgress(null);
                                    setTestRating(null);
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    border: `1px solid ${selected ? 'var(--lm-accent-ink)' : BORDER}`,
                                    borderRadius: CARD_RADIUS,
                                    bgcolor: selected ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                                    p: 1.75,
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }}>{template.title}</Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.75, lineHeight: 1.45 }}>
                                    {template.description}
                                </Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', mt: 1, fontSize: 14 }}>
                                    {template.openingMessage}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    mb={1.25}
                >
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 900 }}>Что выяснить</Typography>
                        <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>
                            Эти поля менеджер будет собирать в диалоге
                        </Typography>
                    </Box>
                    <Button
                        type="button"
                        variant="outlined"
                        startIcon={<IconPlus size={18} />}
                        onClick={addField}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800 }}
                    >
                        Поле
                    </Button>
                </Box>
                <Stack spacing={1}>
                    {fields.map((field) => (
                        <Box
                            key={field.id}
                            display="grid"
                            gridTemplateColumns={{
                                xs: '1fr',
                                md: '1fr',
                            }}
                            gap={1}
                            alignItems="start"
                            sx={{
                                border: `1px solid ${BORDER}`,
                                borderRadius: CARD_RADIUS,
                                p: 1,
                                bgcolor: 'var(--lm-surface)',
                            }}
                        >
                            <Box
                                display="grid"
                                gridTemplateColumns={{ xs: '1fr', md: '190px minmax(0, 1fr)' }}
                                gap={1}
                                minWidth={0}
                            >
                                <TextField
                                    value={field.label}
                                    onChange={(event) => updateField(field.id, { label: event.target.value })}
                                    size="small"
                                    placeholder="Название поля"
                                    fullWidth
                                />
                                <TextField
                                    value={field.question}
                                    onChange={(event) => updateField(field.id, { question: event.target.value })}
                                    size="small"
                                    placeholder="Что уточнить"
                                    fullWidth
                                />
                            </Box>
                            <Box
                                display="flex"
                                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                                gap={0.75}
                            >
                                <Button
                                    type="button"
                                    variant={field.required ? 'contained' : 'outlined'}
                                    onClick={() => updateField(field.id, { required: !field.required })}
                                    sx={{
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {field.required ? 'Обязательно' : 'Опционально'}
                                </Button>
                                <IconButton
                                    size="small"
                                    onClick={() => removeField(field.id)}
                                    disabled={fields.length <= 1}
                                >
                                    <IconTrash size={18} />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Box>

            <Stack spacing={1}>
                <CompactScenarioBlock title="Передача человеку">
                    <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>{currentTemplate.handoffRule}</Typography>
                </CompactScenarioBlock>
                <CompactScenarioBlock title="Follow-up">
                    <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                        Если потенциальный клиент не ответил, менеджер возвращается к диалогу коротким нейтральным
                        сообщением через 24 часа
                    </Typography>
                </CompactScenarioBlock>
                <CompactScenarioBlock title="Стоп-фразы">
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={0.75}
                    >
                        {STOP_PHRASES.map((phrase) => (
                            <Chip
                                key={phrase}
                                size="small"
                                label={phrase}
                                sx={{ bgcolor: 'var(--lm-canvas)', fontWeight: 700 }}
                            />
                        ))}
                    </Box>
                </CompactScenarioBlock>
            </Stack>
        </Stack>
    );

    const renderTestStep = () => (
        <Stack spacing={3}>
            <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 900 }}>Тест AI-менеджера</Typography>
                <Typography sx={{ color: 'var(--lm-muted)', mt: 0.5 }}>
                    Проведите короткий диалог на модели {TEST_MODEL}
                </Typography>
            </Box>

            <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', xl: 'minmax(0, 1.45fr) minmax(300px, 0.55fr)' }}
                gap={1.75}
            >
                <Box
                    sx={{
                        border: `1px solid ${BORDER}`,
                        borderRadius: CARD_RADIUS,
                        bgcolor: 'var(--lm-canvas)',
                        minHeight: 420,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: 1.5,
                            py: 1.25,
                            borderBottom: `1px solid ${BORDER}`,
                            bgcolor: 'var(--lm-surface)',
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 850 }}>Диалог</Typography>
                                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mt: 0.25 }}>
                                    Можно отправить несколько сообщений подряд
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                label={`${testUserMessageCount}/${TEST_MAX_MESSAGES}`}
                                sx={{ bgcolor: 'var(--lm-accent-soft)', color: 'var(--lm-accent)', fontWeight: 800 }}
                            />
                        </Box>
                    </Box>
                    <Stack
                        spacing={1}
                        sx={{ p: 1.5, flex: 1, overflowY: 'auto' }}
                    >
                        {!testMessages.length && (
                            <Typography sx={{ color: 'var(--lm-muted)' }}>
                                Начните с сообщения потенциального клиента. После ответа можно продолжить диалог
                            </Typography>
                        )}
                        {testMessages.map((message) => (
                            <Box
                                key={message.id}
                                alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                                sx={{
                                    maxWidth: '82%',
                                    borderRadius: '14px',
                                    px: 1.5,
                                    py: 1,
                                    bgcolor: message.role === 'user' ? 'var(--lm-accent)' : 'var(--lm-surface)',
                                    color: message.role === 'user' ? 'var(--lm-accent-contrast)' : 'var(--lm-text)',
                                    border: message.role === 'user' ? 'none' : `1px solid ${BORDER}`,
                                }}
                            >
                                <Typography sx={{ fontSize: 15, lineHeight: 1.45 }}>{message.content}</Typography>
                            </Box>
                        ))}
                        {testLoading && (
                            <Box
                                alignSelf="flex-start"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{
                                    maxWidth: '82%',
                                    borderRadius: '14px',
                                    px: 1.5,
                                    py: 1,
                                    bgcolor: 'var(--lm-surface)',
                                    border: `1px solid ${BORDER}`,
                                }}
                            >
                                <CircularProgress
                                    size={16}
                                    sx={{ flexShrink: 0 }}
                                />
                                <Box minWidth={0}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 850 }}>{TEST_WAITING_TITLE}</Typography>
                                    <Typography sx={{ fontSize: 13, color: 'var(--lm-muted)', lineHeight: 1.35 }}>
                                        {TEST_WAITING_HINT}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                    <Box
                        display="grid"
                        gridTemplateColumns="1fr"
                        gap={1}
                        sx={{
                            p: 1.25,
                            borderTop: `1px solid ${BORDER}`,
                            bgcolor: 'var(--lm-surface)',
                        }}
                    >
                        <TextField
                            value={testInput}
                            onChange={(event) => setTestInput(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    handleSendTestMessage();
                                }
                            }}
                            size="small"
                            placeholder={
                                testMessageLimitReached
                                    ? 'Лимит тестового диалога исчерпан'
                                    : testMessages.length
                                    ? 'Следующее сообщение потенциального клиента'
                                    : 'Первое сообщение потенциального клиента'
                            }
                            multiline
                            disabled={testMessageLimitReached}
                            maxRows={4}
                        />
                        <Button
                            type="button"
                            variant="contained"
                            onClick={handleSendTestMessage}
                            disabled={!manager || testLoading || !testInput.trim() || testMessageLimitReached}
                            sx={{
                                borderRadius: '10px',
                                minWidth: 156,
                                height: 40,
                                justifySelf: 'flex-end',
                                whiteSpace: 'nowrap',
                                textTransform: 'none',
                                fontWeight: 900,
                            }}
                        >
                            {testLoading ? 'Ждём ответ' : testMessageLimitReached ? 'Лимит диалога' : 'Отправить'}
                        </Button>
                    </Box>
                </Box>

                <Stack spacing={1.75}>
                    <FieldProgressPanel progress={displayedFieldProgress} />
                    <Box
                        sx={{
                            border: `1px solid ${BORDER}`,
                            borderRadius: CARD_RADIUS,
                            p: 1.75,
                            bgcolor: 'var(--lm-surface)',
                        }}
                    >
                        <Typography sx={{ fontWeight: 850, mb: 1 }}>Оцените тест</Typography>
                        <Typography sx={{ color: 'var(--lm-muted)', fontSize: 14, mb: 1.25 }}>
                            Если оценка 8 или выше, можно создавать менеджера
                        </Typography>
                        <Box
                            display="grid"
                            gridTemplateColumns="repeat(5, 1fr)"
                            gap={0.75}
                        >
                            {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
                                <Button
                                    key={value}
                                    type="button"
                                    variant={testRating === value ? 'contained' : 'outlined'}
                                    onClick={() => setTestRating(value)}
                                    disabled={!hasTestAnswer}
                                    sx={{ minWidth: 0, minHeight: 42, borderRadius: '10px', fontWeight: 900 }}
                                >
                                    {value}
                                </Button>
                            ))}
                        </Box>
                        {testRating !== null && testRating < 8 && (
                            <Stack
                                spacing={1}
                                mt={1.25}
                            >
                                <TextField
                                    value={testImprovement}
                                    onChange={(event) => setTestImprovement(event.target.value)}
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    placeholder="Что нужно улучшить в ответе"
                                />
                                <Button
                                    type="button"
                                    variant="contained"
                                    onClick={handleApplyTestImprovement}
                                    disabled={saving || !testImprovement.trim()}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 900 }}
                                >
                                    Применить и повторить тест
                                </Button>
                            </Stack>
                        )}
                    </Box>
                    <Button
                        type="button"
                        variant="text"
                        onClick={handleRestartTest}
                        disabled={testLoading || saving}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 800 }}
                    >
                        Начать тест заново
                    </Button>
                </Stack>
            </Box>
        </Stack>
    );

    const renderStep = () => {
        if (activeStep === 'knowledge') {
            return renderKnowledgeStep();
        }
        if (activeStep === 'scenario') {
            return renderScenarioStep();
        }
        if (activeStep === 'test') {
            return renderTestStep();
        }
        return renderNameStep();
    };

    const renderActions = () => {
        if (activeStep === 'name') {
            return (
                <Button
                    type="button"
                    variant="contained"
                    onClick={handleContinueName}
                    disabled={saving || !managerNameDraft.trim() || !selectedStreamIds.length}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 900, minHeight: 46, px: 2.5 }}
                >
                    {saving ? (
                        <CircularProgress
                            size={18}
                            color="inherit"
                        />
                    ) : (
                        'Продолжить'
                    )}
                </Button>
            );
        }
        if (activeStep === 'knowledge') {
            const knowledgeReady = hasKnowledgeItems(displayProfile);
            const canAnalyzeWebsite = knowledgeMode === 'website';
            return (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    gap={1.25}
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    flexWrap="wrap"
                >
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={() => manager && goToStep(manager.id, 'name')}
                        disabled={!manager}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, minHeight: 46, px: 2.5 }}
                    >
                        Назад
                    </Button>
                    <Box
                        display="flex"
                        justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
                        alignItems="center"
                        gap={1}
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        sx={{
                            '& > button': {
                                width: { xs: '100%', sm: 'auto' },
                            },
                        }}
                    >
                        {canAnalyzeWebsite && !knowledgeAnalysisError && (
                            <Button
                                type="button"
                                variant={knowledgeReady ? 'outlined' : 'contained'}
                                startIcon={
                                    analyzing ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <IconWand size={18} />
                                    )
                                }
                                onClick={handleAnalyzeWebsite}
                                disabled={saving || analyzing}
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    minHeight: 46,
                                    px: 2.5,
                                }}
                            >
                                {analyzing
                                    ? 'Анализируем сайт'
                                    : knowledgeAnalysisError
                                    ? 'Повторить анализ'
                                    : knowledgeReady
                                    ? websiteUrl.trim()
                                        ? 'Обновить из сайта'
                                        : 'Обновить описание'
                                    : websiteUrl.trim()
                                    ? 'Проанализировать сайт'
                                    : 'Добавить описание'}
                            </Button>
                        )}
                        {!canAnalyzeWebsite && !knowledgeReady && (
                            <Button
                                type="button"
                                variant="contained"
                                disabled
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    minHeight: 46,
                                    px: 2.5,
                                }}
                            >
                                Выберите базу
                            </Button>
                        )}
                        {knowledgeReady && (
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleContinueKnowledge}
                                disabled={saving || analyzing}
                                sx={{
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    minHeight: 46,
                                    px: 2.5,
                                }}
                            >
                                {saving ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                ) : (
                                    'Перейти к сценарию'
                                )}
                            </Button>
                        )}
                    </Box>
                </Box>
            );
        }
        if (activeStep === 'scenario') {
            return (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    gap={1.25}
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    sx={{ width: '100%' }}
                >
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={() => manager && goToStep(manager.id, 'knowledge')}
                        disabled={!manager}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, minHeight: 46, px: 2.5 }}
                    >
                        Назад
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        onClick={handleContinueScenario}
                        disabled={saving || !fields.some((field) => field.required)}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 900, minHeight: 46, px: 2.5 }}
                    >
                        {saving ? (
                            <CircularProgress
                                size={18}
                                color="inherit"
                            />
                        ) : (
                            'Перейти к тесту'
                        )}
                    </Button>
                </Box>
            );
        }
        return (
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={1.25}
                flexDirection={{ xs: 'column', sm: 'row' }}
                sx={{ width: '100%' }}
            >
                <Button
                    type="button"
                    variant="outlined"
                    onClick={() => manager && goToStep(manager.id, 'scenario')}
                    disabled={!manager}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, minHeight: 46, px: 2.5 }}
                >
                    Назад
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    startIcon={
                        saving ? (
                            <CircularProgress
                                size={18}
                                color="inherit"
                            />
                        ) : (
                            <IconSparkles size={18} />
                        )
                    }
                    onClick={handleCreateManager}
                    disabled={saving || !canCreateManager}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 900, minHeight: 46, px: 2.5 }}
                >
                    Создать AI-менеджера
                </Button>
            </Box>
        );
    };

    const content = (
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100%"
            flexGrow={1}
            gap={2.5}
            px={{ xs: 2, md: 0 }}
            py={{ xs: 2, md: 0 }}
        >
            <Box>
                <Typography variant="h1">Запуск AI-менеджера</Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: 'var(--lm-muted)',
                        fontSize: 16,
                        fontWeight: 400,
                        mt: 1.5,
                        lineHeight: 1.5,
                        maxWidth: 760,
                    }}
                >
                    Имя, база знаний, сценарий и тестовый диалог.
                </Typography>
            </Box>

            {errorText && (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: CARD_RADIUS,
                        borderColor: 'var(--lm-danger-border)',
                        bgcolor: 'var(--lm-danger-bg)',
                        p: 2,
                    }}
                >
                    <Typography
                        role="alert"
                        sx={{ color: 'var(--lm-danger-ink)', fontWeight: 600 }}
                    >
                        {errorText}
                    </Typography>
                    {errorText === 'Не удалось загрузить настройку менеджера' && (
                        <Button
                            onClick={() => setLoadAttempt((n) => n + 1)}
                            sx={{ mt: 1 }}
                        >
                            Повторить загрузку
                        </Button>
                    )}
                </Paper>
            )}

            <Paper
                variant="outlined"
                sx={{
                    borderRadius: PANEL_RADIUS,
                    borderColor: BORDER,
                    bgcolor: 'var(--lm-surface)',
                    p: { xs: 2, md: 3 },
                }}
            >
                {loading ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        minHeight={320}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {renderStep()}
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                        >
                            {renderActions()}
                        </Box>
                    </Stack>
                )}
            </Paper>

            <Box display={{ xs: 'block', lg: 'none' }}>
                <SetupProgress
                    activeStep={activeStep}
                    managerName={managerName}
                    completed={completed}
                    creationChecklist={creationChecklist}
                    onReset={handleReset}
                />
            </Box>
        </Box>
    );

    return (
        <DesktopLayout
            rightSidebar={
                <SetupProgress
                    activeStep={activeStep}
                    managerName={managerName}
                    completed={completed}
                    creationChecklist={creationChecklist}
                    onReset={handleReset}
                />
            }
        >
            {content}
        </DesktopLayout>
    );
}
